import { CoinFlightHud } from "../../overlay/coin_flight_hud.js";
import { CoinCounterPill, TopCounterLayout } from "../../common_ui_elements/coin_counter_pill.js";
import { StartupImagePlan } from "../../../image_loader/startup_image_plan.js";
import { Vector2 } from "../../../common/vector2.js";
import { AvatarUtils } from "../../../common/avatar_utils.js";
import { AvatarImageBinder } from "../../../common/avatar_image_binder.js";
import { formatChainOfferRemaining, getChainOfferRemainingMs } from "../chain_offer_screen/chain_offer_timer.js";
import { subtreeHasUnloadedImages } from "../../common_ui_elements/ui_element.js";
import { Game } from "../../../game.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { TransformStraightMove } from "../../../transforms/transform_straight_move.js";
import { TransformScale } from "../../../transforms/transform_scale.js";
import { TransformOpacity } from "../../../transforms/transform_opacity.js";
import { Easing } from "../../../transforms/transform_base.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { ImagesHolder } from "../../../image_loader/images_holder.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIGradientOverlay } from "../../common_ui_elements/ui_gradient_overlay.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UINineSliceElement } from "../../common_ui_elements/ui_nine_slice_element.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { DialogManager } from "../../dialog/dialog_manager.js";
import { SafeAreas } from "../../safe_areas.js";
import { UICart } from "./ui_cart.js";
import { cancelDomAnimation, startDomDrift } from "../../dom_backend/dom_animations.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { ProfileDialog } from "../../dialog/profile_dialog.js";
import { MoreLivesDialog } from "../../dialog/more_lives_dialog.js";
import { explainMissingCampaignLevel } from "../../../level_pack/level_gate.js";
import { PlayDialog, PlayDialogDifficulty, PlayDialogState } from "../../dialog/play_dialog.js";
import { getLevelDifficulty, hasCampaignLevel } from "../../../level_config.js";
import { DailyChallengeService } from "../../../daily_challenge/daily_challenge_service.js";
import { refreshPlayerFromServer } from "../../../api/player_sync.js";
import { heartCountText, heartTimerText, rewardWindowTimerText } from "../../../utils/heart_timer.js";
import { OfferService } from "../../../offers/offer_service.js";
import { canRenderPlacement, openPlacementOffer } from "../../../offers/placement_offer_registry.js";
import { ONE_PLUS_ONE_PLACEMENT, ONE_PLUS_TWO_PLACEMENT, getOnePlusOneState, getOnePlusOneUnclaimedCount, isOnePlusOneOffer, onePlusOneAnalyticsContext } from "../../../offers/one_plus_one_offer.js";
import { MYSTERY_BOX_PLACEMENT, isMysteryBoxOffer, mysteryBoxAnalyticsContext } from "../../../offers/mystery_box_offer.js";
import { countClaimableAdventureRewards } from "../adventure_screen/adventure_floor_info.js";
import { RewardCountBadge } from "../../common_ui_elements/reward_count_badge.js";
import { DailyChallengeCompletedDialog } from "../../dialog/daily_challenge_completed_dialog.js";
import { TournamentDialog } from "../../dialog/tournament_dialog.js";
import { TournamentWaitDialog } from "../../dialog/tournament_wait_dialog.js";
import { TournamentService, type TournamentProgressReward } from "../../../tournament/tournament_service.js";
import { TournamentMenuProgress } from "./tournament_menu_progress.js";
import { DailyTasksService } from "../../../daily_tasks/daily_tasks_service.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import { WinStreakTowerService } from "../../../win_streak_tower/win_streak_tower_service.js";
import type { AdventureBoxReward, AdventureRewardType } from "../../../adventure/adventure_box_rewards.js";
import { EventsConfigService, GameEventID } from "../../../events_config/events_config_service.js";
import { FEATURE_TUTORIAL_BY_EVENT, FeatureTutorialService } from "../../../feature_tutorial/feature_tutorial_service.js";
import { OverlayManager } from "../../overlay/overlay_manager.js";
import { OverlayFactory } from "../../overlay/overlay_factory.js";
import { OverlayId } from "../../overlay/overlay_ids.js";
import type { FeatureTutorialOverlayParams } from "../../overlay/overlays/feature_tutorial_overlay.js";
import type { PrizeShotRewardsOverlayParams } from "../../overlay/overlays/prize_shot_rewards_overlay.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { assignMenuSideSlots, MENU_SIDE_SLOT_IDS, type MenuLayoutConfig, type MenuSideSlotId } from "./menu_side_slots.js";
import { MenuLayoutConfigService } from "../../../menu_layout/menu_layout_config_service.js";
import { KeyHuntService } from "../../../key_hunt/key_hunt_service.js";
import { KeyHuntDialog } from "../../dialog/key_hunt/key_hunt_dialog.js";
import { PRIZE_SHOT_ALWAYS_ON, PrizeShotService } from "../../../prize_shot/prize_shot_service.js";
import { GraphicsSettings } from "../../../graphics/graphics_quality.js";
import { Vfx2DManager } from "../../../webgl_2d/vfx_2d_manager.js";

const DAILY_TASKS_MENU_MAX_AGE_MS = 2000;

export class MenuScreen extends UIElement
{
  private static readonly START_OFFER_ENABLED = false;
  public static active: MenuScreen | null = null;

  private events_controller!: BasicEventsController;

  private cart!: UICart;
  private new_cart?: UICart;
  private is_driving = false;

  private clouds: { el: UIImage; speed: number; w: number }[] = [];
  private cloud_wrap_w = 0;
  private clouds_last_now = 0;

  // Layers below the HUD (sky, clouds, background images, cart): the first
  // children. Cloud drift and the cart's idle animation change only these, so
  // such frames repaint them and blit everything above from fg_cache (see
  // renderLayered).
  private background_elements: UIElement[] = [];
  private bg_only_paint = false;
  private dirty_before_children = false;
  private fg_cache: HTMLCanvasElement | null = null;
  private fg_cache_ctx: CanvasRenderingContext2D | null = null;
  private fg_cache_valid = false;
  private fg_cache_retry_at = 0;
  private static readonly FG_CACHE_RETRY_MS = 250;
  private hud_patches = new Set<UIElement>();
  private static readonly HUD_PATCH_PAD = 16;

  private level: number;
  private intro_pending = true;
  private intro_new_level = false;
  private intro_drive_timer: number | null = null;

  private coins_pill?: CoinCounterPill;
  private heart_timer_text?: UITextElement;
  private hearts_count_text?: UITextElement;
  private hearts_icon?: UIImage;
  private hearts_displayed = -1;
  private hearts_pop_active = false;
  private heart_plus_image?: UIImage;
  private remove_ads_button?: UIImage;
  private remove_ads_timer?: UITextElement;
  private last_remove_ads_timer_text = "";
  private remove_ads_button_visible = false;
  private remove_ads_placement_impression_key?: string;
  private daily_tasks_shortcut?: UIImage;
  private daily_challenge_widget?: UIImage;
  private chain_offer_button?: UIImage;
  private chain_offer_timer?: UITextElement;
  private one_plus_one_offer_timer?: UITextElement;
  private one_plus_one_offer_button?: UIImage;
  private one_plus_one_offer_visible = false;
  private one_plus_one_impression_key?: string;
  private one_plus_one_badge?: RewardCountBadge;
  private one_plus_two_offer_timer?: UITextElement;
  private one_plus_two_offer_button?: UIImage;
  private one_plus_two_offer_visible = false;
  private one_plus_two_impression_key?: string;
  private one_plus_two_badge?: RewardCountBadge;
  private chain_video_button?: UIImage;
  private chain_video_timer?: UITextElement;
  private chain_video_visible: boolean | null = null;
  private last_chain_video_timer_text = "";
  private mystery_box_button?: UIImage;
  private mystery_box_timer?: UITextElement;
  private mystery_box_visible = false;
  private mystery_box_impression_key?: string;
  private last_mystery_box_timer_text = "";
  private key_hunt_button?: UIImage;
  private prize_shot_button?: UIImage;
  private prize_shot_timer?: UITextElement;
  private last_prize_shot_timer_text = "";
  private key_hunt_timer?: UITextElement;
  private key_hunt_badge?: RewardCountBadge;
  private last_key_hunt_timer_text = "";
  private event_widget_states: Partial<Record<GameEventID, "hidden" | "locked" | "unlocked">> = {};
  private event_locked_tooltip: UIElementHolder | null = null;
  private event_locked_dismiss_layer: UIElementHolder | null = null;
  private play_button?: UIImage;
  private booster_tutorial_requested = false;
  private tournament_button?: UIImage;
  private tournament_timer?: UITextElement;
  private tournament_visible: boolean | null = null;
  private last_tournament_timer_text = "";
  private readonly tournament_rewards_offered = new Set<string>();
  private readonly off_tournament_change = TournamentService.onChange( () => this.tournament_progress?.refresh() );
  private tournament_progress?: TournamentMenuProgress;
  private side_slots_signature = "";
  /** Side icons that got a slot in the last layout; the rest are parked off-screen. */
  private placed_side_slots = new Set<MenuSideSlotId>();
  private readonly off_menu_layout_change = MenuLayoutConfigService.onChange( () => this.layoutSideSlots() );
  private avatar_image?: UIImage;
  private avatar_binder?: AvatarImageBinder;
  private shown_hearts = -1;
  private shown_hearts_infinite = false;
  private shown_timer = "";
  private adventure_widget?: UIImage;
  private adventure_timer?: UITextElement;
  private adventure_period_end_ms = 0;
  private last_adventure_timer_text = "";
  private reward_badges: Partial<Record<GameEventID, RewardCountBadge>> = {};
  private next_ui_sync_at = 0;
  private daily_timer?: UITextElement;
  private daily_challenge_timer?: UITextElement;
  private daily_week_end_ms = 0;
  private last_daily_timer_text = "";
  private last_daily_challenge_timer_text = "";
  private last_chain_offer_timer_text = "";
  private last_one_plus_one_offer_timer_text = "";
  private last_one_plus_two_offer_timer_text = "";
  private daily_rollover_attempt_at = 0;
  private adventure_rollover_attempt_at = 0;
  private daily_challenge_rollover_attempt_at = 0;
  private last_auto_refresh_at = 0;
  private play_check_inflight = false;
  private tower_open_inflight = false;
  private readonly feature_ended_queue: Array<{ overlay_id: OverlayId; rewards: AdventureBoxReward[] }> = [];
  private feature_ended_overlay_open = false;

  private readonly on_window_focus = () =>
  {
    void refreshPlayerFromServer();
    void this.refreshWinStreakTower();
    void TournamentService.fetch();
    void this.refreshDailyTasksTimer( true, this.isDailyTasksCacheStale() );
  };
  private readonly on_visibility_change = () =>
  {
    if ( document.visibilityState === "visible" )
    {
      void refreshPlayerFromServer();
      void this.refreshWinStreakTower();
      void TournamentService.fetch();
      void this.refreshDailyTasksTimer( true, this.isDailyTasksCacheStale() );
    }
  };
  private readonly on_entitlements_changed = () => this.syncRemoveAdsButton();
  private readonly on_placements_changed = () =>
  {
    this.syncRemoveAdsButton();
    this.syncOnePlusOneOfferButton();
    this.syncOnePlusTwoOfferButton();
    this.syncMysteryBoxButton();
  };

  constructor( ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, defer_intro = false )
  {
    super( ctx, x, y, width, height, false, false );

    MenuScreen.active = this;

    this.initializeEventsController( ctx.canvas );

    const stats = StatisticsStorage.load();
    this.level = stats.level;
    // Menu isn't visited after every win anymore (winning goes straight into the next
    // level), so instead of a caller-supplied flag we detect "new level(s) since we last
    // showed the menu" ourselves, however many levels were skipped in between.
    const is_new_level = this.level > stats.last_menu_level;
    this.intro_new_level = is_new_level;

    this.createBackground();

    const cart_level = is_new_level ? stats.last_menu_level : this.level;
    const difficulty = getLevelDifficulty( cart_level );
    const car_img_id =
      difficulty === PlayDialogDifficulty.SuperHard ? ImgID.UI_CarSuperHard
        : difficulty === PlayDialogDifficulty.Hard ? ImgID.UI_CarHard
          : ImgID.UI_Car;

    const cart_w = LayoutScaler.widthFromDesign( 719 );
    const cannon_w = LayoutScaler.widthFromDesign( 350 );
    this.cart = this.createCart( this.rect.getW() / 2 - cart_w / 2 + cannon_w / 2, cart_w, cart_level, car_img_id );
    this.addChild( this.cart );
    this.background_elements = [ ...this.getChildren() ];

    this.createTopElements();
    this.createSideShortcuts();
    this.createTournamentButton();
    this.createSettingsButton();
    this.createRemoveAdsButton();
    this.createAdventureWidget();
    this.createDailyChallengeWidget();
    this.createChainOfferButton();
    this.createChainVideoButton();
    this.createMysteryBoxButton();
    this.createKeyHuntButton();
    this.createPrizeShotButton();
    this.createOnePlusOneOfferButton();
    this.createOnePlusTwoOfferButton();
    this.createButton();
    if ( MenuScreen.START_OFFER_ENABLED ) this.createStartOfferButton();
    this.syncRemoveAdsButton();
    this.syncOnePlusOneOfferButton();
    this.syncOnePlusTwoOfferButton();
    this.syncMysteryBoxButton();
    this.syncKeyHuntButton();
    this.syncPrizeShotButton();
    this.syncEventUnlocks();
    this.syncRewardBadges();
    this.layoutSideSlots();

    if ( is_new_level )
    {
      this.play_button?.setScaleImmediate( new Vector2( 0, 0 ) );
      this.play_button?.setClickable( false );
    }

    if ( !defer_intro )
    {
      this.startIntro();
      void refreshPlayerFromServer();
      void this.refreshInitialFeatureState();
    }
    window.addEventListener( "focus", this.on_window_focus );
    document.addEventListener( "visibilitychange", this.on_visibility_change );
    window.addEventListener( "offersEntitlementsChanged", this.on_entitlements_changed );
    window.addEventListener( "offersPlacementsChanged", this.on_placements_changed );
  }

  /** Side-offer columns use the current No Ads inset on both layout widths. */
  private offerColumnLeftX(): number
  {
    return LayoutScaler.xFromLayouts( 32, 57 );
  }

  private offerColumnRightX( element_w: number ): number
  {
    const no_ads_right_edge = LayoutScaler.xFromLayouts( 1048, 1383 );
    return no_ads_right_edge - element_w;
  }

  private static readonly SIDE_SLOT_TOP_DESIGN = 271;
  private static readonly SIDE_SLOT_ROW_DESIGN = 223;

  /** Every side icon has the same purple label plate baked in; its label font matches the No Ads one. */
  private static readonly SIDE_LABEL_FONT_DESIGN = 33;
  private static readonly SIDE_LABEL_MIN_FONT_DESIGN = 26;
  private static readonly SIDE_LABEL_H_DESIGN = 44;

  private static readonly SIDE_ICON_H_DESIGN = MenuScreen.SIDE_SLOT_ROW_DESIGN;
  private static readonly SIDE_ICON_W_DESIGN = MenuScreen.SIDE_ICON_H_DESIGN * 190 / 240;
  private static readonly SIDE_PLATE_CENTER_Y = 201 / 240;
  private static readonly SIDE_PLATE_W = 157 / 190;

  private static sideIconSize(): { w: number; h: number }
  {
    return {
      w: LayoutScaler.widthFromDesign( MenuScreen.SIDE_ICON_W_DESIGN ),
      h: LayoutScaler.heightFromDesign( MenuScreen.SIDE_ICON_H_DESIGN ),
    };
  }

  private static sideLabelRect( icon_w: number, icon_h: number ): { x: number; y: number; w: number; h: number }
  {
    const w = icon_w * MenuScreen.SIDE_PLATE_W * 0.85;
    const h = LayoutScaler.heightFromDesign( MenuScreen.SIDE_LABEL_H_DESIGN );
    return { x: ( icon_w - w ) / 2, y: icon_h * MenuScreen.SIDE_PLATE_CENTER_Y - h / 2, w, h };
  }

  /** The timer on a side icon's plate; every side icon shares this look. */
  private createSideTimer( icon_w: number, icon_h: number ): UITextElement
  {
    const label = MenuScreen.sideLabelRect( icon_w, icon_h );
    return new UITextElement(
      this.ctx,
      label.x,
      label.y,
      label.w,
      label.h,
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( MenuScreen.SIDE_LABEL_FONT_DESIGN ),
        min_font_size: LayoutScaler.textSizeFromDesign( MenuScreen.SIDE_LABEL_MIN_FONT_DESIGN ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.adventure_widget_text,
        shadow_blur: LayoutScaler.widthFromDesign( 3 ),
        shadow_color: ColorScheme.black_transparent_80,
        shadow_offset_x: 0,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        auto_wrap: false,
        scale_to_width: true,
      }
    );
  }

  private sideSlotWidgets(): Record<MenuSideSlotId, { widget: UIImage | undefined; visible: boolean }>
  {
    const event_visible = ( id: GameEventID ): boolean => this.event_widget_states[id] === "locked"
      || this.event_widget_states[id] === "unlocked";
    return {
      win_streak_tower: { widget: this.adventure_widget, visible: event_visible( "win_streak_tower" ) },
      no_ads: { widget: this.remove_ads_button, visible: this.remove_ads_button_visible },
      daily_challenge: { widget: this.daily_challenge_widget, visible: event_visible( "daily_challenge" ) },
      chain_offer: { widget: this.chain_offer_button, visible: event_visible( "chain_offer" ) },
      daily_tasks: { widget: this.daily_tasks_shortcut, visible: event_visible( "daily_tasks" ) },
      chain_video: { widget: this.chain_video_button, visible: this.chain_video_visible === true },
      key_hunt: { widget: this.key_hunt_button, visible: event_visible( "key_hunt" ) },
      prize_shot: { widget: this.prize_shot_button, visible: event_visible( "prize_shot" ) },
      tournament: { widget: this.tournament_button, visible: this.tournament_visible === true },
      mystery_box: { widget: this.mystery_box_button, visible: this.mystery_box_visible },
      one_plus_one: { widget: this.one_plus_one_offer_button, visible: this.one_plus_one_offer_visible },
      one_plus_two: { widget: this.one_plus_two_offer_button, visible: this.one_plus_two_offer_visible },
    };
  }

  /** The server layout; while PRIZE_SHOT_ALWAYS_ON, Prize Shot goes first on the left whatever it says. */
  private static sideSlotsLayout(): MenuLayoutConfig
  {
    const layout = MenuLayoutConfigService.get();
    if ( !PRIZE_SHOT_ALWAYS_ON )
      return layout;
    return {
      ...layout,
      items: { ...layout.items, prize_shot: { side: "left", order: Number.MIN_SAFE_INTEGER, enabled: true } },
    };
  }

  /**
   * Re-deals the visible side icons over the two columns (see menu_side_slots.ts),
   * using the server `menu_layout`. Called whenever one of them shows or hides
   * and when the layout config changes. Icons left without a slot (disabled in
   * the layout or past the last row) are parked off-screen: their own sync
   * keeps driving opacity and clickability, so moving them is what hides them.
   */
  private layoutSideSlots(): void
  {
    const widgets = this.sideSlotWidgets();
    const visible = ( Object.keys( widgets ) as MenuSideSlotId[] )
      .filter( id => widgets[id].visible && widgets[id].widget );
    const slots = assignMenuSideSlots( visible, MenuScreen.sideSlotsLayout() );
    const placed = new Set( slots.map( s => s.id ) );
    const parked = visible.filter( id => !placed.has( id ) );

    const signature = slots.map( s => `${s.id}:${s.column}${s.row}` ).concat( parked.map( id => `${id}:-` ) ).join( "," );
    if ( signature === this.side_slots_signature ) return;
    this.side_slots_signature = signature;
    this.placed_side_slots = placed;
    this.hideEventLockedTooltip();

    for ( const slot of slots )
    {
      const widget = widgets[slot.id].widget!;
      const w = widget.getRect().getW();
      const h = widget.getRect().getH();
      const center_y = LayoutScaler.yFromDesign( MenuScreen.SIDE_SLOT_TOP_DESIGN
        + ( slot.row + 0.5 ) * MenuScreen.SIDE_SLOT_ROW_DESIGN );
      const x = slot.column === "left" ? this.offerColumnLeftX() : this.offerColumnRightX( w );
      widget.setPixelPos( new Vector2( x, Math.round( center_y - h / 2 ) ), true );
      if ( slot.id === "tournament" )
        this.placeTournamentProgress( slot.column );
    }
    for ( const id of parked )
    {
      const widget = widgets[id].widget!;
      widget.setPixelPos( new Vector2( -4 * widget.getRect().getW(), widget.pixelPosImportant().y ), true );
      if ( id === "tournament" )
        this.placeTournamentProgress( "left" );
    }
    this.invalidateSubtree();
  }

  /** False for a side icon the layout left without a slot, so nothing points at it. */
  private isSideIconPlaced( id: GameEventID ): boolean
  {
    if ( !( MENU_SIDE_SLOT_IDS as readonly string[] ).includes( id ) ) return true;
    this.layoutSideSlots();
    return this.placed_side_slots.has( id as MenuSideSlotId );
  }

  private createSideShortcuts(): void
  {
    const { w: daily_w, h: daily_h } = MenuScreen.sideIconSize();
    const daily = new UIImage(
      this.ctx,
      this.offerColumnLeftX(),
      LayoutScaler.yFromDesign( 713 ),
      daily_w,
      daily_h,
      ImgID.UI_DailyTasksShortcut,
      {},
      true
    );
    daily.setOnClick( () =>
    {
      if ( !EventsConfigService.isEventUnlocked( "daily_tasks" ) )
      {
        this.showEventLockedTooltip( daily, EventsConfigService.getUnlockLevel( "daily_tasks" ) );
        return;
      }
      void audioController.playSound( AudioClips.UI_CLICK );
      void ScreenManager.showScreen( Screens.DAILY_TASKS );
    } );
    daily.setOpacityImmediate( 0 );
    daily.setClickable( false );
    this.addChild( daily );
    this.daily_tasks_shortcut = daily;

    const daily_timer = this.createSideTimer( daily_w, daily_h );
    daily.addChild( daily_timer, true );
    this.daily_timer = daily_timer;
    this.attachRewardBadge( "daily_tasks", daily, daily_w );
  }

  private createTournamentButton(): void
  {
    const { w: button_w, h: button_h } = MenuScreen.sideIconSize();
    const button = new UIImage(
      this.ctx,
      this.offerColumnLeftX(),
      LayoutScaler.yFromDesign( 913 ),
      button_w,
      button_h,
      ImgID.UI_MenuTournamentBtn,
      {},
      true
    );
    this.tournament_button = button;
    button.setOnClick( () =>
    {
      const phase = TournamentService.phase();
      if ( !phase ) return;
      void audioController.playSound( AudioClips.UI_CLICK );
      // Tournament art loads right after startup; a tap in the first moment
      // waits for it instead of opening a dialog with blank art.
      StartupImagePlan.runWhenReady( () =>
      {
        if ( MenuScreen.active !== this || DialogManager.isAnyDialogOpened() ) return;
        if ( phase === "announce" )
          TournamentWaitDialog.open();
        else
          DialogManager.openDialog( new TournamentDialog( phase === "claim" ) );
      }, StartupImagePlan.TOURNAMENT_PRIORITY );
    } );
    this.addChild( button );

    const timer = this.createSideTimer( button_w, button_h );
    button.addChild( timer, true );
    this.tournament_timer = timer;

    button.setOpacityImmediate( 0 );
    button.setClickable( false );
    this.createTournamentProgress( button );
    this.syncTournament();
  }

  /** Once a second: button visibility, its timer, and the reward overlays the server says are waiting. */
  private syncTournament(): void
  {
    TournamentService.refreshIfStale();
    const button = this.tournament_button;
    if ( !button ) return;

    const visible = TournamentService.isVisible();
    if ( visible !== this.tournament_visible )
    {
      this.tournament_visible = visible;
      button.setClickable( visible );
      if ( visible ) button.showElement( 0 );
      else button.hideElement( 0 );
      this.layoutSideSlots();
    }

    const phase = TournamentService.phase();
    const text = !visible ? ""
      : phase === "announce" ? formatShortDuration( TournamentService.getMsUntilStart() )
        : phase === "active" ? formatShortDuration( TournamentService.getMsUntilEnd() )
          : LocalizationManager.t( "TOURNAMENT.RESULT" );
    if ( text !== this.last_tournament_timer_text )
    {
      this.last_tournament_timer_text = text;
      this.setHudText( this.tournament_timer, text );
    }

    if ( visible ) this.maybeShowTournamentReward();
  }

  private canShowTournamentOverlay(): boolean
  {
    return MenuScreen.active === this && !this.is_driving
      && !DialogManager.isAnyDialogOpened() && !OverlayManager.isAnyOverlayOpened()
      && StartupImagePlan.isReady( StartupImagePlan.TOURNAMENT_PRIORITY );
  }

  /**
   * Reward overlays open as soon as the menu is back, over the cart still driving in and
   * without waiting for the mini bar's replay (it is skipped: the overlay shows the reward).
   */
  private canShowTournamentRewardOverlay(): boolean
  {
    return MenuScreen.active === this
      && !DialogManager.isAnyDialogOpened() && !OverlayManager.isAnyOverlayOpened()
      && StartupImagePlan.isReady( StartupImagePlan.TOURNAMENT_PRIORITY );
  }

  /** The next reward right after the previous overlay, not on the next once-a-second sync. */
  private showNextTournamentRewardSoon(): void
  {
    setTimeout( () => this.maybeShowTournamentReward(), 0 );
  }

  private maybeShowTournamentReward(): void
  {
    if ( !this.canShowTournamentRewardOverlay() ) return;
    const tournament = TournamentService.current();
    if ( !tournament ) return;

    if ( TournamentService.hasClaimablePlaceReward() )
    {
      const key = `place:${tournament.instance}`;
      if ( this.tournament_rewards_offered.has( key ) ) return;
      this.tournament_rewards_offered.add( key );
      OverlayFactory.openOverlay( OverlayId.TOURNAMENT_WIN_OVERLAY, {
        place: TournamentService.getFinishedPlace(),
        rewards: TournamentService.getPlaceRewards(),
        on_claim: () => TournamentService.claimPlace(),
      } );
      return;
    }

    const next = TournamentService.nextClaimableProgress();
    if ( next )
    {
      this.openTournamentProgressReward( next );
      return;
    }

    if ( TournamentService.isGrandPrizeClaimable() )
    {
      const key = `grand_prize:${tournament.instance}`;
      if ( this.tournament_rewards_offered.has( key ) ) return;
      this.tournament_rewards_offered.add( key );
      this.tournament_progress?.skip();
      OverlayFactory.openOverlay( OverlayId.TOURNAMENT_WIN_OVERLAY, {
        grand_prize: true,
        rewards: TournamentService.getGrandPrizeRewards(),
        on_claim: () => TournamentService.claimGrandPrize(),
      } );
    }
  }

  private openTournamentProgressReward( reward: TournamentProgressReward ): void
  {
    const tournament = TournamentService.current();
    if ( !tournament || !TournamentService.isProgressClaimable( reward ) ) return;
    const key = `progress:${tournament.instance}:${reward.index}`;
    if ( this.tournament_rewards_offered.has( key ) ) return;
    this.tournament_rewards_offered.add( key );
    this.tournament_progress?.skip();
    OverlayFactory.openOverlay( OverlayId.TOURNAMENT_REWARD_OVERLAY, {
      reward,
      on_claim: () => TournamentService.claimProgress( reward.index ),
      // Several checkpoints at once, then the chest: one overlay right after another.
      on_closed: () => this.showNextTournamentRewardSoon(),
    } );
  }

  /** One-row reward path to the right of the tournament button; shows itself only to replay newly won crowns. */
  private createTournamentProgress( button: UIImage ): void
  {
    const button_pos = button.pixelPosImportant();
    const progress = new TournamentMenuProgress(
      this.ctx,
      button_pos.x + button.getRect().getW() - LayoutScaler.widthFromDesign( 10 ),
      button_pos.y + LayoutScaler.heightFromDesign( 45 ),
      () => this.canShowTournamentOverlay(),
      reward => this.openTournamentProgressReward( reward )
    );
    this.addChild( progress );
    this.tournament_progress = progress;
  }

  /** The bar sits beside the button, towards the middle of the screen, whichever column the button landed in. */
  private placeTournamentProgress( column: "left" | "right" ): void
  {
    const button = this.tournament_button;
    const progress = this.tournament_progress;
    if ( !button || !progress ) return;

    const button_pos = button.pixelPosImportant();
    const overlap = LayoutScaler.widthFromDesign( 10 );
    const x = column === "left"
      ? button_pos.x + button.getRect().getW() - overlap
      : button_pos.x - progress.getRect().getW() + overlap;
    progress.setPixelPos( new Vector2( x, button_pos.y + LayoutScaler.heightFromDesign( 45 ) ), true );
  }

  /** Debug: replay the bar after the stub crowns changed. */
  public refreshTournamentProgress(): void
  {
    this.tournament_progress?.refresh();
  }

  private attachRewardBadge( id: GameEventID, widget: UIImage, widget_w: number ): void
  {
    this.reward_badges[id] = this.createCornerBadge( widget, widget_w );
  }

  private createCornerBadge( widget: UIImage, widget_w: number ): RewardCountBadge
  {
    const badge_w = LayoutScaler.widthFromDesign( RewardCountBadge.DESIGN_SIZE );
    const badge = new RewardCountBadge(
      this.ctx,
      widget_w - badge_w + LayoutScaler.xFromDesign( 16 ),
      LayoutScaler.yFromDesign( 8 )
    );
    widget.addChild( badge, true );
    return badge;
  }

  private syncRewardBadges(): void
  {
    const cached_tower = WinStreakTowerService.getCached();
    const counts: Partial<Record<GameEventID, number>> = {
      win_streak_tower: cached_tower?.enabled === true ? countClaimableAdventureRewards( cached_tower.tower ) : 0,
      daily_tasks: DailyTasksService.getClaimableCount(),
      chain_offer: OfferService.getChainOfferClaimableCount(),
    };

    for ( const [ id, badge ] of Object.entries( this.reward_badges ) as [ GameEventID, RewardCountBadge ][] )
    {
      const unlocked = this.event_widget_states[id] === "unlocked";
      badge.setCount( unlocked ? counts[id] ?? 0 : 0 );
    }
  }

  private async refreshInitialFeatureState(): Promise<void>
  {
    // Each ended-feature chest shows as soon as its own payload lands; a
    // slow tournament or daily-tasks fetch must not hold the tower chest back.
    const tower = this.refreshWinStreakTower( false ).then( () =>
    {
      if ( MenuScreen.active !== this ) return;
      this.enqueueAutoClaimedTowerRewards();
      this.showNextFeatureEndedOverlay();
    } );
    const daily = this.refreshDailyTasksTimer( false, this.isDailyTasksCacheStale() ).then( () =>
    {
      if ( MenuScreen.active !== this ) return;
      this.enqueueAutoClaimedDailyTaskRewards();
      this.showNextFeatureEndedOverlay();
    } );
    await Promise.all( [ tower, daily, TournamentService.fetch(), KeyHuntService.fetch() ] );
  }

  private isDailyTasksCacheStale(): boolean
  {
    return DailyTasksService.fetchedAgoMs() > DAILY_TASKS_MENU_MAX_AGE_MS;
  }

  /** Shortcut ribbon counts down to the end of the player's daily-tasks week. */
  private async refreshDailyTasksTimer( collect_auto_claimed = true, force = false ): Promise<void>
  {
    const payload = await DailyTasksService.fetch( force );
    const week_end = payload?.stars?.week_end;
    this.daily_week_end_ms = week_end ? Date.parse( week_end ) : 0;
    this.updateDailyTasksTimer();
    this.syncRewardBadges();

    if ( collect_auto_claimed && MenuScreen.active === this )
    {
      this.enqueueAutoClaimedDailyTaskRewards();
      this.showNextFeatureEndedOverlay();
    }
  }

  private updateDailyTasksTimer(): void
  {
    if ( !this.daily_timer || this.daily_week_end_ms <= 0 ) return;
    if ( this.event_widget_states["daily_tasks"] !== "unlocked" ) return;

    if ( Date.now() >= this.daily_week_end_ms
      && performance.now() - this.daily_rollover_attempt_at > 15_000 )
    {
      this.daily_rollover_attempt_at = performance.now();
      void this.refreshDailyTasksTimer();
    }

    const text = formatShortDuration( this.daily_week_end_ms - Date.now() );
    if ( text === this.last_daily_timer_text ) return;

    this.last_daily_timer_text = text;
    this.setHudText( this.daily_timer, text );
  }

  private async refreshWinStreakTower( collect_auto_claimed = true ): Promise<void>
  {
    const payload = await WinStreakTowerService.fetch();
    const available_tower = payload?.enabled
      && payload.tower
      && payload.tower.floors.length >= 2
      ? payload.tower
      : null;
    const period_end = available_tower?.period_end;
    this.adventure_period_end_ms = period_end ? Date.parse( period_end ) : 0;
    this.updateAdventureTimer();
    this.syncRewardBadges();

    if ( collect_auto_claimed && MenuScreen.active === this )
    {
      this.enqueueAutoClaimedTowerRewards();
      this.showNextFeatureEndedOverlay();
    }
  }

  private enqueueAutoClaimedTowerRewards(): void
  {
    const auto_claimed = WinStreakTowerService.consumeAutoClaimed();
    if ( auto_claimed.length === 0 )
      return;

    const totals = new Map<AdventureRewardType, number>();
    for ( const tower of auto_claimed )
      for ( const reward of tower.rewards )
        totals.set( reward.type, ( totals.get( reward.type ) ?? 0 ) + reward.amount );

    const rewards: AdventureBoxReward[] = Array.from(
      totals,
      ( [ type, amount ] ) => ( { type, amount } )
    );
    if ( rewards.length === 0 )
      return;

    this.feature_ended_queue.push( {
      overlay_id: OverlayId.ADVENTURE_ENDED_OVERLAY,
      rewards,
    } );
  }

  private enqueueAutoClaimedDailyTaskRewards(): void
  {
    const auto_claimed = DailyTasksService.consumeAutoClaimed();
    if ( auto_claimed.length === 0 ) return;

    const totals = new Map<AdventureRewardType, number>();
    for ( const task of auto_claimed )
      for ( const reward of task.rewards )
        totals.set( reward.type, ( totals.get( reward.type ) ?? 0 ) + reward.amount );

    const rewards: AdventureBoxReward[] = Array.from(
      totals,
      ( [ type, amount ] ) => ( { type, amount } )
    );
    if ( rewards.length === 0 ) return;

    this.feature_ended_queue.push( {
      overlay_id: OverlayId.DAILY_TASKS_ENDED_OVERLAY,
      rewards,
    } );
  }

  private showNextFeatureEndedOverlay(): void
  {
    if ( MenuScreen.active !== this || this.feature_ended_overlay_open ) return;
    if ( this.feature_ended_queue.length > 0 && !StartupImagePlan.isReady( StartupImagePlan.EVENT_OVERLAYS_PRIORITY ) )
    {
      StartupImagePlan.runWhenReady( () => this.showNextFeatureEndedOverlay(), StartupImagePlan.EVENT_OVERLAYS_PRIORITY );
      return;
    }

    const next = this.feature_ended_queue.shift();
    if ( !next ) return;

    this.feature_ended_overlay_open = true;
    const overlay = OverlayFactory.openOverlay( next.overlay_id, {
      rewards: next.rewards,
      on_closed: () =>
      {
        this.feature_ended_overlay_open = false;
        this.showNextFeatureEndedOverlay();
      },
    } );

    if ( !overlay )
    {
      this.feature_ended_overlay_open = false;
      this.showNextFeatureEndedOverlay();
    }
  }

  private updateAdventureTimer(): void
  {
    if ( !this.adventure_timer ) return;
    if ( this.event_widget_states["win_streak_tower"] !== "unlocked" ) return;

    if ( this.adventure_period_end_ms > 0
      && Date.now() >= this.adventure_period_end_ms
      && performance.now() - this.adventure_rollover_attempt_at > 15_000 )
    {
      this.adventure_rollover_attempt_at = performance.now();
      void this.refreshWinStreakTower();
    }

    const text = this.adventure_period_end_ms > 0
      ? formatShortDuration( this.adventure_period_end_ms - Date.now() )
      : "";
    if ( text === this.last_adventure_timer_text ) return;
    this.last_adventure_timer_text = text;
    this.setHudText( this.adventure_timer, text );
  }

  private createSettingsButton(): void
  {
    const w = LayoutScaler.widthFromDesign( 125 );
    const h = LayoutScaler.heightFromDesign( 125 );
    const x = LayoutScaler.xFromLayouts( 930, 1265 );
    const y = LayoutScaler.yFromDesign( 110 );

    const btn = new UIImage(
      this.ctx,
      x,
      y,
      w,
      h,
      ImgID.UI_MenuSettingsIcon,
      {},
      true
    );
    btn.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      Game.instance.openSettingsDialog();
    } );
    this.addChild( btn );
  }

  private createDailyChallengeWidget(): void
  {
    const { w, h } = MenuScreen.sideIconSize();
    const x = this.offerColumnLeftX();
    const y = LayoutScaler.yFromDesign( 504 );

    const widget = new UIImage(
      this.ctx,
      x,
      y,
      w,
      h,
      ImgID.UI_DailyChallengeWidget,
      {},
      true
    )
    this.addChild( widget );

    const text = this.createSideTimer( w, h );
    widget.addChild( text, true );
    this.daily_challenge_timer = text;

    const refresh_timer = (): void =>
    {
      this.updateDailyChallengeTimer();
    };
    refresh_timer();

    widget.setOnClick( () =>
    {
      if ( !EventsConfigService.isEventUnlocked( "daily_challenge" ) )
      {
        this.showEventLockedTooltip( widget, EventsConfigService.getUnlockLevel( "daily_challenge" ) );
        return;
      }
      const open = (): void =>
      {
        refresh_timer();
        if ( DailyChallengeService.isFinished() )
          DailyChallengeCompletedDialog.open();
        else
          void ScreenManager.showScreen( Screens.DAILY_CHALLENGE );
      };
      if ( DailyChallengeService.getCached() )
      {
        open();
        void DailyChallengeService.fetch().then( refresh_timer );
      }
      else
      {
        void DailyChallengeService.fetch().then( open );
      }
    } );

    widget.setOpacityImmediate( 0 );
    widget.setClickable( false );
    this.daily_challenge_widget = widget;
  }

  private updateDailyChallengeTimer(): void
  {
    if ( !this.daily_challenge_timer ) return;
    if ( this.event_widget_states["daily_challenge"] !== "unlocked" ) return;

    if ( DailyChallengeService.isPeriodOver()
      && performance.now() - this.daily_challenge_rollover_attempt_at > 15_000 )
    {
      this.daily_challenge_rollover_attempt_at = performance.now();
      void DailyChallengeService.fetch().then( () => this.updateDailyChallengeTimer() );
    }

    const seconds = DailyChallengeService.getSecondsUntilReset();
    const text = seconds === null ? "" : formatShortDuration( seconds * 1000 );
    if ( text === this.last_daily_challenge_timer_text ) return;

    this.last_daily_challenge_timer_text = text;
    this.setHudText( this.daily_challenge_timer, text );
  }
  

  private createChainOfferButton(): void
  {
    const { w: button_w, h: button_h } = MenuScreen.sideIconSize();
    const button_x = this.offerColumnRightX( button_w );
    const button_y = LayoutScaler.yFromDesign( 500 );

    const chain_offer_button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_ChainOfferIcon,
      {},
      true
    );

    const timer_text = this.createSideTimer( button_w, button_h );
    chain_offer_button.addChild( timer_text, true );
    this.chain_offer_timer = timer_text;
    this.attachRewardBadge( "chain_offer", chain_offer_button, button_w );

    this.updateChainOfferTimer();

    chain_offer_button.setOnClick( () =>
    {
      if ( !EventsConfigService.isEventUnlocked( "chain_offer" ) )
      {
        this.showEventLockedTooltip( chain_offer_button, EventsConfigService.getUnlockLevel( "chain_offer" ) );
        return;
      }
      void audioController.playSound( AudioClips.UI_CLICK );
      void ScreenManager.showScreen( Screens.CHAIN_OFFER );
    } );

    chain_offer_button.setOpacityImmediate( 0 );
    chain_offer_button.setClickable( false );
    this.addChild( chain_offer_button );
    this.chain_offer_button = chain_offer_button;
  }

  private createOnePlusOneOfferButton(): void
  {
    const { w: button_w, h: button_h } = MenuScreen.sideIconSize();
    const button_x = this.offerColumnRightX( button_w );
    const button_y = LayoutScaler.yFromDesign( 1149 );

    const button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_OnePlusOneOfferIcon,
      {},
      true
    );

    const timer = this.createSideTimer( button_w, button_h );
    button.addChild( timer, true );
    this.one_plus_one_offer_timer = timer;

    // Placement-driven like the No Ads shortcut: hidden until the server
    // resolves a lobby_one_plus_one offer the player still has steps in.
    button.setOpacityImmediate( 0 );
    button.setClickable( false );

    button.setOnClick( () =>
    {
      const resolved = OfferService.getPlacementOffer( ONE_PLUS_ONE_PLACEMENT );
      if ( !resolved || !isOnePlusOneOffer( resolved.offer ) ) return;
      void audioController.playSound( AudioClips.UI_CLICK );
      openPlacementOffer( ONE_PLUS_ONE_PLACEMENT );
    } );

    this.addChild( button );
    this.one_plus_one_offer_button = button;
    this.one_plus_one_badge = this.createCornerBadge( button, button_w );
  }

  private syncOnePlusOneOfferButton(): void
  {
    const resolved = OfferService.getPlacementOffer( ONE_PLUS_ONE_PLACEMENT );
    const offer = resolved && isOnePlusOneOffer( resolved.offer ) ? resolved.offer : null;
    const state = getOnePlusOneState( offer );
    const remaining_ms = offer ? this.onePlusOneRemainingMs( offer ) : null;
    // A bought offer stays in the lobby, past its timer, until the player
    // takes every free step on the offer screen.
    const bought = Boolean( state && !state.awaiting_purchase );
    const visible = Boolean( offer && state && !state.all_claimed && canRenderPlacement( resolved!.template )
      && ( bought || remaining_ms === null || remaining_ms > 0 ) );

    this.updateOnePlusOneOfferTimer( remaining_ms, bought );
    this.one_plus_one_badge?.setCount( visible ? getOnePlusOneUnclaimedCount( offer ) : 0 );
    this.setOnePlusOneOfferButtonVisible( visible, visible ? resolved : null );
  }

  private setOnePlusOneOfferButtonVisible(
    visible: boolean,
    placement: ReturnType<typeof OfferService.getPlacementOffer> | null
  ): void
  {
    this.one_plus_one_offer_button?.setClickable( visible && placement !== null );

    if ( visible && placement )
    {
      const impression_key = `${placement.offer.id}:${placement.template}`;
      if ( this.one_plus_one_impression_key !== impression_key )
      {
        this.one_plus_one_impression_key = impression_key;
        void OfferService.trackPlacementImpression( placement.offer.id, onePlusOneAnalyticsContext( ONE_PLUS_ONE_PLACEMENT ) );
      }
    }

    if ( visible === this.one_plus_one_offer_visible ) return;
    this.one_plus_one_offer_visible = visible;
    this.layoutSideSlots();
    if ( visible )
      this.one_plus_one_offer_button?.showElement( 0 );
    else
      this.one_plus_one_offer_button?.hideElement( 0 );
  }

  /** Milliseconds left by the server clock, or null for an offer without an end. */
  private onePlusOneRemainingMs( offer: ReturnType<typeof OfferService.getOffer> ): number | null
  {
    if ( !offer ) return null;
    const deadline_ms = OfferService.getOfferDisplayEndMs( offer );
    if ( deadline_ms === null ) return null;
    return deadline_ms - ( OfferService.getServerNowMs() ?? Date.now() );
  }

  private createMysteryBoxButton(): void
  {
    const { w: button_w, h: button_h } = MenuScreen.sideIconSize();
    const button = new UIImage(
      this.ctx,
      this.offerColumnRightX( button_w ),
      LayoutScaler.yFromDesign( 926 ),
      button_w,
      button_h,
      ImgID.UI_MysteryBoxOfferIcon,
      {},
      true
    );

    const timer = this.createSideTimer( button_w, button_h );
    button.addChild( timer, true );
    this.mystery_box_timer = timer;

    // Placement-driven like 1+1: hidden until the server resolves a lobby_mystery_box offer.
    button.setOpacityImmediate( 0 );
    button.setClickable( false );

    button.setOnClick( () =>
    {
      const resolved = OfferService.getPlacementOffer( MYSTERY_BOX_PLACEMENT );
      if ( !resolved || !isMysteryBoxOffer( resolved.offer ) ) return;
      void audioController.playSound( AudioClips.UI_CLICK );
      openPlacementOffer( MYSTERY_BOX_PLACEMENT );
    } );

    this.addChild( button );
    this.mystery_box_button = button;
  }

  private syncMysteryBoxButton(): void
  {
    const resolved = OfferService.getPlacementOffer( MYSTERY_BOX_PLACEMENT );
    const offer = resolved && isMysteryBoxOffer( resolved.offer ) ? resolved.offer : null;
    const remaining_ms = offer ? this.onePlusOneRemainingMs( offer ) : null;
    const visible = Boolean( offer && canRenderPlacement( resolved!.template ) && ( remaining_ms === null || remaining_ms > 0 ) );

    const text = !visible || remaining_ms === null ? "" : formatShortDuration( Math.max( 0, remaining_ms ) );
    if ( text !== this.last_mystery_box_timer_text )
    {
      this.last_mystery_box_timer_text = text;
      this.setHudText( this.mystery_box_timer, text );
    }

    this.mystery_box_button?.setClickable( visible );
    if ( visible && resolved )
    {
      const impression_key = `${resolved.offer.id}:${resolved.template}`;
      if ( this.mystery_box_impression_key !== impression_key )
      {
        this.mystery_box_impression_key = impression_key;
        void OfferService.trackPlacementImpression( resolved.offer.id, mysteryBoxAnalyticsContext( MYSTERY_BOX_PLACEMENT ) );
      }
    }

    if ( visible === this.mystery_box_visible ) return;
    this.mystery_box_visible = visible;
    this.layoutSideSlots();
    if ( visible )
      this.mystery_box_button?.showElement( 0 );
    else
      this.mystery_box_button?.hideElement( 0 );
  }

  /** Key Hunt: a daily server event (see key_hunt_service.ts); the purple plate counts down the day. */
  private createKeyHuntButton(): void
  {
    const { w: button_w, h: button_h } = MenuScreen.sideIconSize();
    const button = new UIImage(
      this.ctx,
      this.offerColumnLeftX(),
      LayoutScaler.yFromDesign( 926 ),
      button_w,
      button_h,
      ImgID.UI_KeyHuntLobbyIcon,
      {},
      true
    );

    const timer = this.createSideTimer( button_w, button_h );
    button.addChild( timer, true );
    this.key_hunt_timer = timer;
    this.key_hunt_badge = this.createCornerBadge( button, button_w );

    button.setOpacityImmediate( 0 );
    button.setClickable( false );
    button.setOnClick( () =>
    {
      if ( !EventsConfigService.isEventUnlocked( "key_hunt" ) )
      {
        this.showEventLockedTooltip( button, EventsConfigService.getUnlockLevel( "key_hunt" ) );
        return;
      }
      if ( !KeyHuntService.isActive() ) return;
      void audioController.playSound( AudioClips.UI_CLICK );
      KeyHuntDialog.open();
    } );

    this.addChild( button );
    this.key_hunt_button = button;
  }

  private syncKeyHuntButton(): void
  {
    this.syncEventWidget( "key_hunt", this.key_hunt_button, this.key_hunt_timer,
      () => { this.last_key_hunt_timer_text = ""; }, () => KeyHuntService.hasEvent() );
    const active = this.event_widget_states["key_hunt"] === "unlocked" && KeyHuntService.isActive();
    this.key_hunt_badge?.setCount( active ? KeyHuntService.claimableCount() : 0 );
    if ( !active ) return;
    const text = formatShortDuration( KeyHuntService.timeLeftMs() );
    if ( text === this.last_key_hunt_timer_text ) return;
    this.last_key_hunt_timer_text = text;
    this.setHudText( this.key_hunt_timer, text );
  }

  /** Prize Shot: shoot balloons for prizes (see prize_shot_service.ts); the plate counts down the round. */
  private createPrizeShotButton(): void
  {
    const { w: button_w, h: button_h } = MenuScreen.sideIconSize();
    const button = new UIImage(
      this.ctx,
      this.offerColumnLeftX(),
      LayoutScaler.yFromDesign( 926 ),
      button_w,
      button_h,
      ImgID.UI_PrizeShotLobbyIcon,
      {},
      true
    );

    const timer = this.createSideTimer( button_w, button_h );
    button.addChild( timer, true );
    this.prize_shot_timer = timer;

    button.setOpacityImmediate( 0 );
    button.setClickable( false );
    button.setOnClick( () => this.onPrizeShotClick( button ) );

    this.addChild( button );
    this.prize_shot_button = button;
  }

  private onPrizeShotClick( button: UIImage ): void
  {
    if ( !PRIZE_SHOT_ALWAYS_ON && !EventsConfigService.isEventUnlocked( "prize_shot" ) )
    {
      this.showEventLockedTooltip( button, EventsConfigService.getUnlockLevel( "prize_shot" ) );
      return;
    }

    // Prizes the last period left unclaimed are handed over first.
    if ( this.maybeShowPrizeShotPending() )
      return;
    if ( !PrizeShotService.isActive() )
      return;

    void audioController.playSound( AudioClips.UI_CLICK );
    void ScreenManager.showScreen( Screens.GAME, { prize_shot: true } );
  }

  private syncPrizeShotButton(): void
  {
    this.syncEventWidget(
      "prize_shot",
      this.prize_shot_button,
      this.prize_shot_timer,
      () => { this.last_prize_shot_timer_text = ""; },
      () => PRIZE_SHOT_ALWAYS_ON || PrizeShotService.getConfig().enabled,
      PRIZE_SHOT_ALWAYS_ON
    );
    if ( this.event_widget_states["prize_shot"] !== "unlocked" )
      return;

    this.maybeShowPrizeShotPending();

    const text = formatShortDuration( PrizeShotService.timeLeftMs() );
    if ( text === this.last_prize_shot_timer_text )
      return;

    this.last_prize_shot_timer_text = text;
    this.setHudText( this.prize_shot_timer, text );
  }

  /** Prizes found but not taken before the period ended come with the rewards overlay. Returns whether it opened. */
  private maybeShowPrizeShotPending(): boolean
  {
    if ( MenuScreen.active !== this || DialogManager.isAnyDialogOpened() || OverlayManager.isAnyOverlayOpened() )
      return false;
    if ( !StartupImagePlan.isReady( StartupImagePlan.GAME_PRIORITY ) )
      return false;

    const rewards = PrizeShotService.pendingRewards();
    if ( rewards.length === 0 )
      return false;

    OverlayFactory.openOverlay( OverlayId.PRIZE_SHOT_REWARDS_OVERLAY, {
      rewards,
      on_claim: () => PrizeShotService.claimPending(),
    } as PrizeShotRewardsOverlayParams );
    return true;
  }

  private createOnePlusTwoOfferButton(): void
  {
    const { w: button_w, h: button_h } = MenuScreen.sideIconSize();
    const button_x = this.offerColumnRightX( button_w );
    const button_y = LayoutScaler.yFromDesign( 1372 );

    const button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_OnePlusTwoOfferIcon,
      {},
      true
    );

    const timer = this.createSideTimer( button_w, button_h );
    button.addChild( timer, true );
    this.one_plus_two_offer_timer = timer;

    button.setOpacityImmediate( 0 );
    button.setClickable( false );

    button.setOnClick( () =>
    {
      const resolved = OfferService.getPlacementOffer( ONE_PLUS_TWO_PLACEMENT );
      if ( !resolved || !isOnePlusOneOffer( resolved.offer ) ) return;
      void audioController.playSound( AudioClips.UI_CLICK );
      openPlacementOffer( ONE_PLUS_TWO_PLACEMENT );
    } );

    this.addChild( button );
    this.one_plus_two_offer_button = button;
    this.one_plus_two_badge = this.createCornerBadge( button, button_w );
  }

  private syncOnePlusTwoOfferButton(): void
  {
    const resolved = OfferService.getPlacementOffer( ONE_PLUS_TWO_PLACEMENT );
    const offer = resolved && isOnePlusOneOffer( resolved.offer ) ? resolved.offer : null;
    const state = getOnePlusOneState( offer );
    const remaining_ms = offer ? this.onePlusOneRemainingMs( offer ) : null;
    const bought = Boolean( state && !state.awaiting_purchase );
    const visible = Boolean( offer && state && !state.all_claimed && canRenderPlacement( resolved!.template )
      && ( bought || remaining_ms === null || remaining_ms > 0 ) );

    this.updateOnePlusTwoOfferTimer( remaining_ms, bought );
    this.one_plus_two_badge?.setCount( visible ? getOnePlusOneUnclaimedCount( offer ) : 0 );
    this.setOnePlusTwoOfferButtonVisible( visible, visible ? resolved : null );
  }

  private setOnePlusTwoOfferButtonVisible(
    visible: boolean,
    placement: ReturnType<typeof OfferService.getPlacementOffer> | null
  ): void
  {
    this.one_plus_two_offer_button?.setClickable( visible && placement !== null );

    if ( visible && placement )
    {
      const impression_key = `${placement.offer.id}:${placement.template}`;
      if ( this.one_plus_two_impression_key !== impression_key )
      {
        this.one_plus_two_impression_key = impression_key;
        void OfferService.trackPlacementImpression( placement.offer.id, onePlusOneAnalyticsContext( ONE_PLUS_TWO_PLACEMENT ) );
      }
    }

    if ( visible === this.one_plus_two_offer_visible ) return;
    this.one_plus_two_offer_visible = visible;
    this.layoutSideSlots();
    if ( visible )
      this.one_plus_two_offer_button?.showElement( 0 );
    else
      this.one_plus_two_offer_button?.hideElement( 0 );
  }

  private createChainVideoButton(): void
  {
    const { w: button_w, h: button_h } = MenuScreen.sideIconSize();
    const button_x = this.offerColumnRightX( button_w );
    const button_y = LayoutScaler.yFromDesign( 713 );

    const chain_video_button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_ChainVideoIcon,
      {},
      true
    );

    const timer_text = this.createSideTimer( button_w, button_h );
    chain_video_button.addChild( timer_text, true );
    this.chain_video_timer = timer_text;

    chain_video_button.setOnClick( () =>
    {
      if ( !OfferService.getChainOffers( 'chain_video' ).length ) return;
      void audioController.playSound( AudioClips.UI_CLICK );
      void ScreenManager.showScreen( Screens.CHAIN_VIDEO );
    } );

    chain_video_button.setOpacityImmediate( 0 );
    chain_video_button.setClickable( false );
    this.addChild( chain_video_button );
    this.chain_video_button = chain_video_button;
    this.syncChainVideoButton();
  }

  private syncChainVideoButton(): void
  {
    const button = this.chain_video_button;
    if ( !button ) return;
    const visible = OfferService.getChainOffers( 'chain_video' ).length > 0;
    this.updateChainVideoTimer( visible );
    if ( visible === this.chain_video_visible ) return;
    this.chain_video_visible = visible;
    this.layoutSideSlots();
    button.setClickable( visible );
    if ( visible ) button.showElement( 0 );
    else button.hideElement( 0 );
  }

  private updateChainVideoTimer( visible: boolean ): void
  {
    if ( !this.chain_video_timer ) return;
    const text = visible ? formatChainOfferRemaining( getChainOfferRemainingMs( 'chain_video' ) ) : "";
    if ( text === this.last_chain_video_timer_text ) return;
    this.last_chain_video_timer_text = text;
    this.setHudText( this.chain_video_timer, text );
  }

  private createAdventureWidget(): void
  {
    const { w, h } = MenuScreen.sideIconSize();
    const x = this.offerColumnLeftX();
    const y = LayoutScaler.yFromDesign( 271 );

    const widget = new UIImage(
      this.ctx,
      x,
      y,
      w,
      h,
      ImgID.UI_AdventureWidget,
      {},
      true
    )
    this.addChild( widget );

    const text = this.createSideTimer( w, h );
    widget.addChild( text, true );
    this.adventure_timer = text;
    this.attachRewardBadge( "win_streak_tower", widget, w );

    widget.setOnClick( async () =>
    {
      if ( !EventsConfigService.isEventUnlocked( "win_streak_tower" ) )
      {
        this.showEventLockedTooltip( widget, EventsConfigService.getUnlockLevel( "win_streak_tower" ) );
        return;
      }
      if ( this.tower_open_inflight ) return;
      this.tower_open_inflight = true;
      try
      {
        await ScreenManager.showScreen( Screens.ADVENTURE );
      }
      finally
      {
        this.tower_open_inflight = false;
      }
    } );

    widget.setOpacityImmediate( 0 );
    widget.setClickable( false );
    this.adventure_widget = widget;
  }

  private createRemoveAdsButton(): void
  {
    const { w: button_w, h: button_h } = MenuScreen.sideIconSize();
    const button_x = this.offerColumnRightX( button_w );
    const button_y = LayoutScaler.yFromDesign( 284 );

    const remove_ads_button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_MenuRemoveAdsIcon,
      {},
      true
    );

    const timer = this.createSideTimer( button_w, button_h );
    remove_ads_button.addChild( timer, true );
    this.remove_ads_timer = timer;

    remove_ads_button.setOpacityImmediate( 0 );
    remove_ads_button.setClickable( false );

    remove_ads_button.setOnClick( () =>
    {
      const resolved_placement = OfferService.getPlacementOffer( 'lobby_no_ads' );
      if ( OfferService.hasNoAds() || !resolved_placement
        || ( !OfferService.isFreeOffer( resolved_placement.offer.id ) && !OfferService.getPlatformProductId( resolved_placement.offer.id ) ) ) return;
      void audioController.playSound( AudioClips.UI_CLICK );
      openPlacementOffer( 'lobby_no_ads' );
    } );

    this.addChild( remove_ads_button );
    this.remove_ads_button = remove_ads_button;
  }

  private syncRemoveAdsButton(): void
  {
    // The permanent entitlement retires the shortcut for good. A timed no_ads
    // reward instead borrows it as a badge, the way the heart icon turns into
    // the infinite-hearts countdown: same icon, remaining window underneath.
    if ( OfferService.hasNoAds() )
    {
      this.setRemoveAdsButtonVisible( false, null );
      this.updateRemoveAdsTimer( undefined );
      return;
    }

    const resolved_placement = OfferService.getPlacementOffer( 'lobby_no_ads' );
    const offer_visible = Boolean( resolved_placement
      && OfferService.isOfferLive( resolved_placement.offer )
      && canRenderPlacement( resolved_placement.template )
      && ( OfferService.isFreeOffer( resolved_placement.offer.id ) || OfferService.getPlatformProductId( resolved_placement.offer.id ) ) );
    const no_ads_window_active = StatisticsStorage.load().no_ads_until > Date.now();

    this.updateRemoveAdsTimer( resolved_placement?.offer );
    this.setRemoveAdsButtonVisible( offer_visible || no_ads_window_active, offer_visible ? resolved_placement! : null );
  }

  /** Shown either as a purchasable placement (clickable, impression tracked) or as a plain badge. */
  private setRemoveAdsButtonVisible(
    visible: boolean,
    placement: ReturnType<typeof OfferService.getPlacementOffer> | null
  ): void
  {
    this.remove_ads_button?.setClickable( placement !== null );

    if ( visible && placement )
    {
      const impression_key = `${placement.offer.id}:${placement.template}`;
      if ( this.remove_ads_placement_impression_key !== impression_key )
      {
        this.remove_ads_placement_impression_key = impression_key;
        void OfferService.trackPlacementImpression( placement.offer.id, {
          placement: 'lobby_no_ads',
          placement_id: 'lobby_no_ads',
          template: placement.template,
        } );
      }
    }

    if ( visible === this.remove_ads_button_visible ) return;
    this.remove_ads_button_visible = visible;
    this.layoutSideSlots();
    if ( visible )
      this.remove_ads_button?.showElement( 0 );
    else
      this.remove_ads_button?.hideElement( 0 );
  }

  private updateRemoveAdsTimer( offer: ReturnType<typeof OfferService.getOffer> | undefined ): void
  {
    if ( !this.remove_ads_timer ) return;

    // An active timed window wins over the offer's own countdown: while ads
    // are already off, the time that matters is how long that lasts.
    const now_ms = Date.now();
    const no_ads_until = StatisticsStorage.load().no_ads_until;
    const deadline_ms = offer ? OfferService.getOfferDisplayEndMs( offer ) : null;
    const server_now_ms = OfferService.getServerNowMs() ?? now_ms;
    const text = no_ads_until > now_ms
      ? rewardWindowTimerText( no_ads_until, now_ms )
      : deadline_ms === null
        ? ""
        : formatShortDuration( deadline_ms - server_now_ms );

    if ( text === this.last_remove_ads_timer_text ) return;
    this.last_remove_ads_timer_text = text;
    this.setHudText( this.remove_ads_timer, text );
    this.invalidateSubtree();
  }

  private updateChainOfferTimer(): void
  {
    if ( !this.chain_offer_timer ) return;
    if ( this.event_widget_states["chain_offer"] !== "unlocked" ) return;

    const text = formatChainOfferRemaining( getChainOfferRemainingMs() );
    if ( text === this.last_chain_offer_timer_text ) return;

    this.last_chain_offer_timer_text = text;
    this.setHudText( this.chain_offer_timer, text );
  }

  private updateOnePlusOneOfferTimer( remaining_ms: number | null, bought: boolean ): void
  {
    if ( !this.one_plus_one_offer_timer ) return;

    const text = bought && ( remaining_ms === null || remaining_ms <= 0 )
      ? LocalizationManager.t( "CLAIM" )
      : remaining_ms === null ? "" : formatShortDuration( Math.max( 0, remaining_ms ) );
    if ( text === this.last_one_plus_one_offer_timer_text ) return;

    this.last_one_plus_one_offer_timer_text = text;
    this.setHudText( this.one_plus_one_offer_timer, text );
  }

  private updateOnePlusTwoOfferTimer( remaining_ms: number | null, bought: boolean ): void
  {
    if ( !this.one_plus_two_offer_timer ) return;

    const text = bought && ( remaining_ms === null || remaining_ms <= 0 )
      ? LocalizationManager.t( "CLAIM" )
      : remaining_ms === null ? "" : formatShortDuration( Math.max( 0, remaining_ms ) );
    if ( text === this.last_one_plus_two_offer_timer_text ) return;

    this.last_one_plus_two_offer_timer_text = text;
    this.setHudText( this.one_plus_two_offer_timer, text );
  }

  private syncEventUnlocks(): void
  {
    this.syncEventWidget( "daily_tasks", this.daily_tasks_shortcut, this.daily_timer,
      () => { this.last_daily_timer_text = ""; void this.refreshDailyTasksTimer(); } );
    this.syncEventWidget( "daily_challenge", this.daily_challenge_widget, this.daily_challenge_timer,
      () => { this.last_daily_challenge_timer_text = ""; void DailyChallengeService.fetch().then( () => this.updateDailyChallengeTimer() ); } );
    this.syncEventWidget( "chain_offer", this.chain_offer_button, this.chain_offer_timer,
      () => { this.last_chain_offer_timer_text = ""; this.updateChainOfferTimer(); },
      () => OfferService.getChainOffers().length > 0 );
    this.syncEventWidget( "win_streak_tower", this.adventure_widget, this.adventure_timer,
      () => { this.last_adventure_timer_text = ""; this.updateAdventureTimer(); } );
  }

  private syncEventWidget(
    id: GameEventID,
    widget: UIImage | undefined,
    label: UITextElement | undefined,
    on_unlocked: () => void,
    has_content: () => boolean = () => true,
    force_available = false
  ): void
  {
    const config = EventsConfigService.get()[id];
    const state = force_available ? "unlocked"
      : !EventsConfigService.isLoaded() || !config.enabled || !has_content() ? "hidden"
        : EventsConfigService.isEventUnlocked( id ) ? "unlocked" : "locked";
    if ( state === this.event_widget_states[id] ) return;
    this.event_widget_states[id] = state;
    this.hideEventLockedTooltip();
    this.layoutSideSlots();

    if ( !widget ) return;

    if ( state === "hidden" )
    {
      widget.setClickable( false );
      widget.hideElement( 0 );
      return;
    }

    widget.setDimmed( state === "locked" );
    widget.setClickable( true );
    widget.showElement( 0 );
    if ( state === "locked" )
      label?.setText( LocalizationManager.tWithParams( "MENU.EVENT_LOCKED_LVL", [ String( config.min_level ) ] ) );
    else
      on_unlocked();
  }

  private static readonly FEATURE_TUTORIAL_WIDGET_IMAGES: Partial<Record<GameEventID, ImgID>> = {
    daily_challenge: ImgID.UI_DailyChallengeWidget,
    daily_tasks: ImgID.UI_DailyTasksShortcut,
    chain_offer: ImgID.UI_ChainOfferIcon,
    win_streak_tower: ImgID.UI_AdventureWidget,
  };

  private static readonly OPTIONAL_FEATURE_TUTORIALS: ReadonlySet<GameEventID> = new Set<GameEventID>( [ "daily_challenge" ] );

  private featureTutorialWidgets(): Partial<Record<GameEventID, UIImage | undefined>>
  {
    return {
      daily_challenge: this.daily_challenge_widget,
      daily_tasks: this.daily_tasks_shortcut,
      chain_offer: this.chain_offer_button,
      win_streak_tower: this.adventure_widget,
    };
  }

  // Once the player confirms a tutorial, the menu is about to navigate into
  // the feature; the transition is async, so for a few ticks the menu is still
  // the current screen and its sync would otherwise chain the NEXT pending
  // tutorial right on top of the opening event. The flag lives on the
  // instance: returning to the menu rebuilds the screen, unblocking the chain.
  private feature_tutorial_navigation_pending = false;

  private openFeatureTutorialFor( event_id: GameEventID, widget: UIImage, mark_completed: boolean ): void
  {
    const rect = widget.getAbsoluteRect();
    const timer = widget.getChildren().find( child => child instanceof UITextElement );
    const timer_rect = timer?.getRect();
    const timer_pos = timer?.pixelPosForRender();

    OverlayFactory.openOverlay( OverlayId.FEATURE_TUTORIAL_OVERLAY, {
      img_id: MenuScreen.FEATURE_TUTORIAL_WIDGET_IMAGES[event_id]!,
      rect,
      ...( timer && timer_rect && timer_pos
        ? {
          timer: {
            get_text: () => timer.getText(),
            x: timer_pos.x,
            y: timer_pos.y,
            width: timer_rect.getW(),
            height: timer_rect.getH(),
            daily_tasks_style: event_id === "daily_tasks",
          },
        }
        : {} ),
      on_confirm: () =>
      {
        if ( mark_completed )
        {
          const tutorial_id = FEATURE_TUTORIAL_BY_EVENT[event_id];
          if ( tutorial_id !== undefined )
            FeatureTutorialService.complete( tutorial_id );
        }
        this.feature_tutorial_navigation_pending = true;
        widget.getOnClick()?.( new Vector2(
          rect.x + rect.width / 2,
          rect.y + rect.height / 2
        ) );
      },
      dismissible: MenuScreen.OPTIONAL_FEATURE_TUTORIALS.has( event_id ),
      on_dismiss: () =>
      {
        const tutorial_id = FEATURE_TUTORIAL_BY_EVENT[event_id];
        if ( mark_completed && tutorial_id !== undefined && MenuScreen.OPTIONAL_FEATURE_TUTORIALS.has( event_id ) )
          FeatureTutorialService.complete( tutorial_id );
      },
    } as FeatureTutorialOverlayParams );
  }

  public debugStartFeatureTutorial( event_id: GameEventID ): boolean
  {
    const widget = this.featureTutorialWidgets()[event_id];
    if ( !widget || MenuScreen.FEATURE_TUTORIAL_WIDGET_IMAGES[event_id] === undefined )
      return false;

    this.openFeatureTutorialFor( event_id, widget, false );
    return true;
  }

  private feature_tutorial_input_blocked = false;

  private pendingFeatureTutorialEvent(): GameEventID | undefined
  {
    const widgets = this.featureTutorialWidgets();

    return ( Object.keys( widgets ) as GameEventID[] )
      .filter( id =>
      {
        const tutorial_id = FEATURE_TUTORIAL_BY_EVENT[id];
        return tutorial_id !== undefined
          && this.event_widget_states[id] === "unlocked"
          && widgets[id] !== undefined
          && this.isSideIconPlaced( id )
          && !FeatureTutorialService.isCompleted( tutorial_id );
      } )
      .sort( ( a, b ) => EventsConfigService.getUnlockLevel( a ) - EventsConfigService.getUnlockLevel( b ) )[ 0 ];
  }

  private blockInputWhileFeatureTutorialPending(): void
  {
    if ( this.pendingFeatureTutorialEvent() === undefined )
      return;

    this.feature_tutorial_input_blocked = true;
    this.setClickable( false );
    Game.instance.setNavigationClickable( false );
  }

  private unblockInputForFeatureTutorial(): void
  {
    if ( !this.feature_tutorial_input_blocked )
      return;

    this.feature_tutorial_input_blocked = false;
    this.setClickable( true );
    Game.instance.setNavigationClickable( true );
  }

  private maybeStartFeatureTutorial(): void
  {
    if ( this.is_driving || this.event_locked_tooltip || this.play_check_inflight )
      return;
    if ( this.feature_tutorial_navigation_pending )
      return;
    if ( ScreenManager.getCurrentScreen() !== Screens.MENU )
      return;
    if ( !EventsConfigService.isLoaded() )
      return;
    if ( DialogManager.isAnyDialogOpened() || OverlayManager.isAnyOverlayOpened() )
      return;

    const event_id = this.pendingFeatureTutorialEvent();
    if ( !event_id )
    {
      this.maybeStartBoosterTutorial();
      return;
    }

    this.openFeatureTutorialFor( event_id, this.featureTutorialWidgets()[event_id]!, true );
  }

  private maybeStartBoosterTutorial(): void
  {
    if ( this.booster_tutorial_requested || !this.play_button || !PlayDialog.isBoosterTutorialPending() )
      return;

    this.booster_tutorial_requested = true;
    OverlayFactory.openOverlay( OverlayId.NEW_BOOSTER_OVERLAY, {
      booster_id: PlayDialog.boosterTutorialId(),
      on_claim: () => PlayDialog.claimBoosterTutorial(),
    } );
  }

  private showEventLockedTooltip( widget: UIImage, unlock_level: number ): void
  {
    this.hideEventLockedTooltip();

    const clamp = ( value: number, min: number, max: number ) => Math.min( Math.max( value, min ), max );
    const widget_rect = widget.getAbsoluteRect();

    const tape_w = LayoutScaler.widthFromDesign( 675 );
    const tape_h = LayoutScaler.heightFromDesign( 200 );
    const bottom_w = LayoutScaler.widthFromDesign( 110.46 );
    const bottom_h = LayoutScaler.heightFromDesign( 77.12 );
    const side_margin = LayoutScaler.widthFromDesign( 33 );
    const widget_center_x = widget_rect.x + widget_rect.width / 2;

    const tape_x = clamp(
      widget_center_x - tape_w / 2,
      side_margin,
      this.rect.getW() - side_margin - tape_w
    );
    const tape_y = widget_rect.y - LayoutScaler.heightFromDesign( 219.75 );
    const bottom_x = clamp( widget_center_x - tape_x - bottom_w / 2, 0, tape_w - bottom_w );
    const bottom_y = LayoutScaler.heightFromDesign( 167.68 );

    const tooltip = new UIElementHolder(
      this.ctx,
      tape_x,
      tape_y,
      tape_w,
      Math.max( tape_h, bottom_y + bottom_h )
    );
    tooltip.setClickable( false );

    const dismiss_layer = new UIElementHolder(
      this.ctx,
      0,
      0,
      this.rect.getW(),
      this.rect.getH()
    );
    dismiss_layer.setOnClick( () => this.hideEventLockedTooltip(), true );

    const tape = new UINineSliceElement(
      this.ctx,
      0,
      0,
      tape_w,
      tape_h,
      ImgID.UI_GameBoosterLockedTape,
      "booster_tape"
    );
    tooltip.addChild( tape );

    const text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 30 ),
      LayoutScaler.yFromDesign( 20 ),
      tape_w - LayoutScaler.widthFromDesign( 60 ),
      tape_h - LayoutScaler.heightFromDesign( 40 ),
      {
        text: LocalizationManager.tWithParams( "MENU.EVENT_UNLOCKS_AT", [ String( unlock_level ) ] ),
        font_size: LayoutScaler.textSizeFromDesign( 52 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 10 ),
        outline_color: ColorScheme.continue_overlay_caption_outline,
        fit_font_size: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 30 ),
        line_height: 0.95,
        auto_wrap: true,
      }
    );
    tape.addChild( text );

    const bottom = new UIImage(
      this.ctx,
      bottom_x,
      bottom_y,
      bottom_w,
      bottom_h,
      ImgID.UI_GameBoosterBottom
    );
    tooltip.addChild( bottom );

    this.event_locked_dismiss_layer = dismiss_layer;
    this.addChild( dismiss_layer );
    this.event_locked_tooltip = tooltip;
    this.addChild( tooltip );
    this.invalidateSubtree();
  }

  private hideEventLockedTooltip(): void
  {
    if ( !this.event_locked_tooltip && !this.event_locked_dismiss_layer ) return;

    if ( this.event_locked_tooltip )
      this.removeChild( this.event_locked_tooltip );
    if ( this.event_locked_dismiss_layer )
      this.removeChild( this.event_locked_dismiss_layer );

    this.event_locked_tooltip = null;
    this.event_locked_dismiss_layer = null;
    this.invalidateSubtree();
  }

  private static readonly BG_DESIGN_W = 1280;
  private static readonly BG_DESIGN_H = 2106;

  private static readonly BG_GROUND_RATIO = 0.4;
  private static readonly BG_HORIZON_RATIO = 0.25;
  private static readonly BG_FRAME_H_PER_W = 1.5;
  private static readonly CIRCUS_OVER_PLAZA = 0.887;
  private static readonly MOUNTAINS_WIDTH_RATIO = 1.164;
  private static readonly MOUNTAINS_TOP_RATIO = 0.204;

  private createClouds( sky_w: number, sky_h: number ): void
  {
    const max_cloud_w = LayoutScaler.widthFromDesign( 436 );
    const max_cloud_h = LayoutScaler.heightFromDesign( 280 );
    const layout_scale = LayoutScaler.getScale();

    this.cloud_wrap_w = sky_w;

    for ( let i = 0; i < 7; i++ )
    {
      const scale = 0.6 + Math.random() * 0.4;

      const cloud_w = max_cloud_w * scale;
      const cloud_h = max_cloud_h * scale;

      const cloud = new UIImage(
        this.ctx,
        Math.random() * ( sky_w - cloud_w ),
        Math.random() * Math.max( 0, sky_h - cloud_h ),
        cloud_w,
        cloud_h,
        ImgID.UI_MenuCloud
      );

      this.addChild( cloud );

      const cloud_speed = ( 10 + ( 1 - scale ) * 55 + Math.random() * 18 ) * layout_scale;
      this.clouds.push( { el: cloud, speed: cloud_speed, w: cloud_w } );
    }
  }

  private createBackground(): void
  {
    const circus_img = ImagesHolder.getImage( ImgID.UI_MenuCircus );
    const plaza_img = ImagesHolder.getImage( ImgID.UI_MenuPlaza );
    const mountains_img = ImagesHolder.getImage( ImgID.UI_MenuMountains );

    const screen_w = this.rect.getW();
    const screen_h = this.rect.getH();

    // cover: беремо більший коефіцієнт, щоб картинка гарантовано перекрила екран
    const design_w = LayoutScaler.widthFromDesign( MenuScreen.BG_DESIGN_W );
    const design_h = LayoutScaler.heightFromDesign( MenuScreen.BG_DESIGN_H );

    const fit = Math.max( screen_w / design_w, screen_h / design_h ) * 1.035;

    const bg_w = design_w * fit;
    const bg_h = bg_w * MenuScreen.BG_FRAME_H_PER_W;

    // якір: та сама точка, від якої рахується машина
    const anchor_y = this.rect.getH2() - LayoutScaler.yFromDesign( 150 );

    const bg_x = ( screen_w - bg_w ) / 2;
    const bg_y = anchor_y - bg_h * MenuScreen.BG_GROUND_RATIO;

    // ---- небо ----

    const sky_h = bg_y + bg_h * MenuScreen.BG_HORIZON_RATIO + Game.safeAreaTop();

    const sky = new UIGradientOverlay(
      this.ctx,
      0,
      -Game.safeAreaTop(),
      screen_w,
      sky_h,
      "#0067F3",
      "#E5FCFF"
    );

    this.addChild( sky );

    this.createClouds( screen_w, sky_h );

    const plaza_h = bg_w * ( plaza_img.height / plaza_img.width );
    const plaza_y = bg_y + bg_h - plaza_h;

    const circus_h = bg_w * ( circus_img.height / circus_img.width );
    const circus_y = plaza_y - circus_h * MenuScreen.CIRCUS_OVER_PLAZA;

    const mountains_w = bg_w * MenuScreen.MOUNTAINS_WIDTH_RATIO;
    const mountains_h = mountains_w * ( mountains_img.height / mountains_img.width );
    const mountains_x = bg_x - ( mountains_w - bg_w ) / 2;
    const mountains_y = circus_y + circus_h * MenuScreen.MOUNTAINS_TOP_RATIO;

    const mountains = new UIImage( this.ctx, mountains_x, mountains_y, mountains_w, mountains_h, ImgID.UI_MenuMountains );
    this.addChild( mountains );

    const circus = new UIImage( this.ctx, bg_x, circus_y, bg_w, circus_h, ImgID.UI_MenuCircus );
    this.addChild( circus );

    const plaza = new UIImage( this.ctx, bg_x, plaza_y, bg_w, plaza_h, ImgID.UI_MenuPlaza );
    this.addChild( plaza );
  }

  private createCart( x: number, w: number, level: number, img_id: ImgID ): UICart
  {
    const cart_w = w;
    const cart_h = LayoutScaler.heightFromDesign( 801 );
    const cart_x = x;
    const cart_y = this.rect.getH2() - cart_h / 2 - LayoutScaler.yFromDesign( 50 );

    const cart = new UICart(
      this.ctx,
      cart_x,
      cart_y,
      cart_w,
      cart_h,
      level,
      img_id
    );

    // this.addChild( cart );
    return cart;
  }

  private createTopElements(): void
  {
    this.createAvatar();

    const stats = StatisticsStorage.load();
    const now_ms = Date.now();
    const hearts_infinite = stats.infinite_hearts_until > now_ms;

    this.createHeartElement(
      TopCounterLayout.heartX(),
      TopCounterLayout.y(),
      hearts_infinite ? ImgID.UI_ShopInfiniteHeart : ImgID.IconHeart,
      heartTimerText( stats, now_ms ),
      hearts_infinite ? "" : heartCountText( stats, now_ms )
    );

    const coins_pill = new CoinCounterPill( this.ctx, TopCounterLayout.coinX(), TopCounterLayout.y(), { clickable: true } );
    coins_pill.setOnClick( async () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      await ScreenManager.showScreen( Screens.SHOP );
      Game.instance.updateNavigationBlock();
    } );
    this.addChild( coins_pill );
    this.coins_pill = coins_pill;
    CoinFlightHud.dock( coins_pill );
  }

  private createAvatar(): void
  {
    const circle_x = LayoutScaler.xFromDesign( 30 );
    const circle_y = LayoutScaler.yFromDesign( 62 );
    const circle_size = LayoutScaler.widthFromDesign( 220 );
    const open_profile_dialog = () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      ProfileDialog.open();
    };
    const avatar_button = new UIElementHolder(
      this.ctx,
      circle_x,
      circle_y,
      circle_size,
      circle_size,
      false,
      true
    );
    avatar_button.setOnClick( open_profile_dialog );
    this.addChild( avatar_button );

    const bg_circle = new RoundedRectangle(
      this.ctx,
      0,
      0,
      circle_size,
      circle_size,
      circle_size / 2,
      {
        type: "linear-gradient",
        from: "#F9F4C9",
        to: "#B5A084",
        direction: "vertical"
      }
    );
    bg_circle.setClickable( false );
    avatar_button.addChild( bg_circle );

    const avatar_id = Number.parseInt( localStorage.getItem( "player_avatar_id" ) ?? "8", 10 );
    const avatar_size = LayoutScaler.widthFromDesign( 200 );
    const avatar_inset = ( circle_size - avatar_size ) / 2;
    const avatar = new UIImage(
      this.ctx,
      avatar_inset,
      avatar_inset,
      avatar_size,
      avatar_size,
      AvatarUtils.getAvatarImgId( Number.isFinite( avatar_id ) ? avatar_id : 8 ),
      {},
      false,
      avatar_size / 2
    );
    avatar.setClickable( false );
    avatar_button.addChild( avatar );

    this.avatar_image = avatar;
    this.avatar_binder = new AvatarImageBinder( avatar );
    this.avatar_binder.applyFromStorage( 8 );
  }

  private createHeartElement( x: number, y: number, img_id: ImgID, count: string, heart_count: string ): void
  {
    const m = TopCounterLayout.metrics();

    // One hit area and one press transform for the complete widget, including
    // the part of the heart icon that extends beyond the background.
    const holder = new UIElementHolder(
      this.ctx,
      x + m.holder_left,
      y + m.holder_top,
      m.holder_w,
      m.holder_h,
      false,
      true
    );
    holder.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      MoreLivesDialog.open();
    } );
    this.addChild( holder );

    const bg = new UINineSliceElement(
      this.ctx,
      -m.holder_left,
      -m.holder_top,
      m.bg_w,
      m.bg_h,
      ImgID.UI_MenuBlockBg,
      "menu_top_bg"
    );
    bg.setClickable( false );
    holder.addChild( bg );

    const count_text = TopCounterLayout.createCountText( this.ctx, m, count );
    bg.addChild( count_text );
    this.heart_timer_text = count_text;

    const icon = new UIImage( this.ctx, m.icon_x, m.icon_y, m.icon_w, m.icon_h, img_id, {}, false );
    bg.addChild( icon );

    const heart_count_text = new UITextElement(
      this.ctx,
      0,
      LayoutScaler.yFromDesign( -7 ),
      m.icon_w,
      m.icon_h,
      {
        text: heart_count,
        font_size: LayoutScaler.textSizeFromDesign( 70 ),
        font_weight: 900,
        text_color: ColorScheme.white,
        text_style: TextStyleScheme.geologica,
        shadow_blur: LayoutScaler.widthFromDesign( 6 ),
        shadow_color: ColorScheme.black_transparent,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
      }
    );
    icon.addChild( heart_count_text );
    heart_count_text.setClickable( false );
    this.hearts_count_text = heart_count_text;
    this.hearts_icon = icon;

    const plus = new UIImage( this.ctx, m.plus_x, m.plus_y, m.plus_w, m.plus_h, ImgID.IconPlus, {}, false );
    icon.addChild( plus );

    this.heart_plus_image = plus;
    const hearts_infinite = StatisticsStorage.load().infinite_hearts_until > Date.now();
    plus.setOpacityImmediate( hearts_infinite || count === "MAX" ? 0 : 1 );
  }

  private animateNewLevel(): void
  {
    this.driveCarts( this.cart, this.level, true );
  }

  // Debug helper: drive the current cart off-screen and roll a fresh one in
  // showing the SAME level (no level change) — for previewing the drive animation.
  public replayCartDrive(): void
  {
    this.driveCarts( this.new_cart ?? this.cart, this.level );
  }

  private driveCarts( outgoing: UICart, incoming_level: number, reveal_play_button = false ): void
  {
    if ( this.is_driving )
      return;

    this.is_driving = true;

    const y = outgoing.pixelPosForRender().y;
    const x = outgoing.pixelPosForRender().x;
    const cart_w = outgoing.getRect().getW();

    const move_out = new TransformStraightMove(
      outgoing,
      true,
      null,
      new Vector2( this.rect.getW() + LayoutScaler.widthFromDesign( 400 ), y ),
      performance.now() + 560,
      640,
      { easing: Easing.EaseInQuad },
    )

    outgoing.moveWheels();
    outgoing.bakeDomRide( performance.now(), 1e9, 1360 );
    outgoing.addTransform( move_out );

    const difficulty = getLevelDifficulty( incoming_level );
    const car_img_id =
      difficulty === PlayDialogDifficulty.SuperHard ? ImgID.UI_CarSuperHard
        : difficulty === PlayDialogDifficulty.Hard ? ImgID.UI_CarHard
          : ImgID.UI_Car;

    const incoming = this.createCart( -cart_w * 1.5, cart_w, incoming_level, car_img_id );
    this.addChildAtIndex( incoming, this.childIndexOf( outgoing ) + 1 );

    const move_in = new TransformStraightMove(
      incoming,
      true,
      null,
      new Vector2( x, y ),
      performance.now() + 880,
      480,
      { easing: Easing.EaseOutQuad },
    )

    incoming.moveWheels();
    incoming.bakeDomRide( performance.now(), 1300, 1360 );
    incoming.addTransform( move_in );

    setTimeout( () =>
    {
      incoming.tiltBeforeStop();
    }, 1300 );

    move_in.setOnFinished( () =>
    {
      outgoing.stopWheels();
      this.removeChild( outgoing );

      incoming.stopWheels();

      this.cart = incoming;
      // The new cart now sits where the old one was: it joins the layers
      // below the HUD, and the cached HUD no longer contains it.
      this.background_elements = this.background_elements.map( element => element === outgoing ? incoming : element );
      this.fg_cache_valid = false;
      this.new_cart = undefined;
      this.is_driving = false;

      if ( reveal_play_button )
        this.revealPlayButton();

      this.unblockInputForFeatureTutorial();
      this.maybeStartFeatureTutorial();
    } );
  }

  // Pop the Play button in with the same scale-overshoot used for buttons
  // revealed inside dialogs (e.g. PlayDialog's close button).
  private revealPlayButton(): void
  {
    const btn = this.play_button;
    if ( !btn )
      return;

    const now = performance.now();
    const pop_up_duration = 176;
    const pop_settle_duration = 112;

    btn.addTransform( new TransformScale(
      btn, true,
      new Vector2( 0, 0 ), new Vector2( 1.25, 1.25 ),
      now, pop_up_duration,
      { easing: Easing.EaseOutQuad }
    ) );

    btn.addTransform( new TransformScale(
      btn, true,
      new Vector2( 1.25, 1.25 ), new Vector2( 1.0, 1.0 ),
      now + pop_up_duration, pop_settle_duration,
      { easing: Easing.EaseOutQuad }
    ) );

    btn.setClickable( true );
  }

  private createButton(): void
  {
    const w = LayoutScaler.widthFromDesign( 598 );
    const h = LayoutScaler.heightFromDesign( 274 );
    const x = this.rect.getW() / 2 - w / 2;
    const y = this.rect.getH() - h - LayoutScaler.yFromDesign( 420 );

    const difficulty = getLevelDifficulty( this.level );
    const button_img_id =
      difficulty === PlayDialogDifficulty.SuperHard ? ImgID.UI_RedBtn
      : difficulty === PlayDialogDifficulty.Hard ? ImgID.UI_PurpleFrameBtn
      : ImgID.UI_PlayButton;

    const button = new UIImage(
      this.ctx,
      x,
      y,
      w,
      h,
      button_img_id,
      {},
      true
    );

    button.setOnClick( () =>
    {
      if ( this.play_check_inflight )
        return;

      if ( !hasCampaignLevel( this.level ) )
      {
        explainMissingCampaignLevel( this.level );
        return;
      }

      if ( StatisticsStorage.load().hearts <= 0 )
      {
        MoreLivesDialog.open();
        return;
      }

      void audioController.playSound( AudioClips.SEARCH_LEVEL );
      this.play_check_inflight = true;
      void this.verifyHeartsAndPlay();
    } );

    this.addChild( button );
    this.play_button = button;

    const is_difficult = difficulty !== PlayDialogDifficulty.Common;
    const outline_color =
      difficulty === PlayDialogDifficulty.SuperHard ? ColorScheme.menu_play_btn_outline_super_hard
      : difficulty === PlayDialogDifficulty.Hard ? ColorScheme.more_lives_purple_outline
      : ColorScheme.more_lives_buy_outline;
    const text_style = {
      font_weight: 900,
      text_color: ColorScheme.white,
      text_style: TextStyleScheme.geologica,
      outline_color,
      outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
      outline_offset_y: LayoutScaler.yFromDesign( 2 ),
      ...TextStyleScheme.buttonShadow(),
      text_align: MyTextAlign.Center,
      vertical_align: MyTextVerticalAlign.Center,
      scale_to_width: true,
      auto_wrap: false,
    };

    const play_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 70 ),
      LayoutScaler.yFromDesign( is_difficult ? 75 : -2 ),
      w - LayoutScaler.widthFromDesign( 140 ),
      LayoutScaler.heightFromDesign( is_difficult ? 72 : 274 ),
      {
        localization_key: "PLAY",
        font_size: LayoutScaler.textSizeFromDesign( is_difficult ? 62 : 80 ),
        ...text_style,
      }
    );
    button.addChild( play_text, true );

    if ( is_difficult )
    {
      const difficulty_text = new UITextElement(
        this.ctx,
        LayoutScaler.xFromDesign( 55 ),
        LayoutScaler.yFromDesign( 144 ),
        w - LayoutScaler.widthFromDesign( 110 ),
        LayoutScaler.heightFromDesign( 68 ),
        {
          text: difficulty === PlayDialogDifficulty.SuperHard ? "Super Hard!" : "Hard!",
          font_size: LayoutScaler.textSizeFromDesign( 48 ),
          ...text_style,
        }
      );
      button.addChild( difficulty_text, true );
    }
  }

  // Same icon+timer shape as createChainOfferButton() — sits in the same
  // right-side column, using the same offerColumnRightX() helper as
  // chain_offer/remove_ads so it lines up with them.
  // No EventsConfigService gating: start_offer isn't a GameEventID (no server
  // config for it yet), so the icon is just always shown.
  private createStartOfferButton(): void
  {
    const button_w = LayoutScaler.widthFromDesign( 182 );
    const button_h = LayoutScaler.heightFromDesign( 205 );
    const button_x = this.offerColumnRightX( button_w );
    const button_y = LayoutScaler.yFromDesign( 889 );

    const start_offer_button = new UIImage(
      this.ctx,
      button_x,
      button_y,
      button_w,
      button_h,
      ImgID.UI_StartOfferIcon,
      {},
      true
    );

    const text_w = LayoutScaler.widthFromDesign( 120 );
    const text_h = LayoutScaler.heightFromDesign( 44 );
    const text_x = ( button_w - text_w ) / 2;
    const text_y = LayoutScaler.yFromDesign( 145 );

    // TODO: static placeholder — wire to the real offer's expiry once
    // start_offer has a server-driven countdown (see OFFER_TIMER_TEXT in
    // start_offer_screen.ts, same stub).
    const timer_text = new UITextElement(
      this.ctx,
      text_x,
      text_y,
      text_w,
      text_h,
      {
        text: "23:59:59",
        font_size: LayoutScaler.textSizeFromDesign( 27 ),
        font_weight: 900,
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.adventure_widget_text,
        shadow_blur: LayoutScaler.widthFromDesign( 3 ),
        shadow_color: ColorScheme.black_transparent_80,
        shadow_offset_y: LayoutScaler.yFromDesign( 3 ),
        auto_wrap: false,
        scale_to_width: true,
      },
    );
    start_offer_button.addChild( timer_text, true );

    start_offer_button.setOnClick( () =>
    {
      void audioController.playSound( AudioClips.UI_CLICK );
      void ScreenManager.showScreen( Screens.START_OFFER );
    } );

    this.addChild( start_offer_button );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );

    this.events_controller.init(
      ( p: Vector2 ) => super.onTouchStart( p, performance.now() ),
      ( p: Vector2 ) => super.onTouchEnd( p, performance.now() ),
      ( p: Vector2 ) => super.onTouchMove( p, performance.now() )
    );
  }

  private startIntro(): void
  {
    if ( !this.intro_pending )
      return;
    this.intro_pending = false;

    if ( this.intro_new_level )
    {
      this.blockInputWhileFeatureTutorialPending();
      StatisticsStorage.update( { last_menu_level: this.level } );
      const coins_land_in = Vfx2DManager.getLatestCoinFlyFinishTime() - performance.now();
      if ( coins_land_in > 0 )
        this.intro_drive_timer = window.setTimeout( () =>
        {
          this.intro_drive_timer = null;
          this.animateNewLevel();
        }, coins_land_in );
      else
        this.animateNewLevel();
    }
    else
      this.maybeStartFeatureTutorial();
  }

  public canReuseScreen(): boolean
  {
    const stats = StatisticsStorage.load();
    if ( this.intro_pending )
      return stats.level === this.level;
    return !this.is_driving
      && this.intro_drive_timer === null
      && !this.feature_tutorial_input_blocked
      && !this.feature_ended_overlay_open
      && stats.level === this.level
      && stats.level <= stats.last_menu_level;
  }

  public onScreenHidden(): void
  {
    if ( MenuScreen.active === this )
      MenuScreen.active = null;
    if ( this.coins_pill )
      CoinFlightHud.undock( this.coins_pill );
    this.events_controller?.disable();
  }

  public onScreenShown(): void
  {
    MenuScreen.active = this;
    if ( this.coins_pill )
      CoinFlightHud.dock( this.coins_pill );
    this.events_controller?.enable();
    this.next_ui_sync_at = 0;
    this.startIntro();
    void refreshPlayerFromServer();
    void this.refreshInitialFeatureState();
  }

  public destroy(): void
  {
    if ( this.intro_drive_timer !== null )
    {
      clearTimeout( this.intro_drive_timer );
      this.intro_drive_timer = null;
    }
    for ( const anim of this.cloud_anims )
      cancelDomAnimation( anim );
    this.cloud_anims = [];
    if ( MenuScreen.active === this )
      MenuScreen.active = null;
    if ( this.coins_pill )
      CoinFlightHud.undock( this.coins_pill );

    this.unblockInputForFeatureTutorial();
    window.removeEventListener( "focus", this.on_window_focus );
    this.off_tournament_change();
    this.off_menu_layout_change();
    document.removeEventListener( "visibilitychange", this.on_visibility_change );
    window.removeEventListener( "offersEntitlementsChanged", this.on_entitlements_changed );
    window.removeEventListener( "offersPlacementsChanged", this.on_placements_changed );

    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null as any;
    }
  }

  private async verifyHeartsAndPlay(): Promise<void>
  {
    try
    {
      // Booster counts are server-owned. Wait for the shared refresh instead
      // of opening the play dialog with the inventory's initial zero values.
      await refreshPlayerFromServer();

      this.level = StatisticsStorage.load().level;
      if ( !hasCampaignLevel( this.level ) )
      {
        await ScreenManager.showScreen( Screens.MENU );
        explainMissingCampaignLevel( this.level );
        return;
      }

      if ( StatisticsStorage.load().hearts <= 0 )
      {
        await ScreenManager.showScreen( Screens.MENU );
        MoreLivesDialog.open();
        return;
      }

      await ScreenManager.showScreen( Screens.MENU );
      PlayDialog.open( this.level, PlayDialogState.Play, getLevelDifficulty( this.level ) );
    }
    catch ( error )
    {
      await ScreenManager.showScreen( Screens.MENU );
      console.error( "[MenuScreen] Failed to prepare game", error );
    }
    finally
    {
      this.play_check_inflight = false;
    }
  }

  private syncTopBar(): void
  {
    const stats = StatisticsStorage.load();
    const now_ms = Date.now();

    this.coins_pill?.setCoins( stats.coins );

    // While the infinite-hearts window is active the counter reads "∞": the
    // digit pop animation is meaningless there, so the text is set directly
    // and the tracked values stay in sync for when the window ends.
    const hearts_infinite = stats.infinite_hearts_until > now_ms;
    if ( hearts_infinite !== this.shown_hearts_infinite )
    {
      this.shown_hearts_infinite = hearts_infinite;
      this.shown_hearts = stats.hearts;
      this.hearts_displayed = stats.hearts;
      this.hearts_icon?.setImageId( hearts_infinite ? ImgID.UI_ShopInfiniteHeart : ImgID.IconHeart );
      this.hearts_count_text?.setText( hearts_infinite ? "" : heartCountText( stats, now_ms ) );
      this.setShouldBeRendered();
    }
    else if ( hearts_infinite )
    {
      this.shown_hearts = stats.hearts;
      this.hearts_displayed = stats.hearts;
    }
    else if ( stats.hearts !== this.shown_hearts )
    {
      const had_value = this.shown_hearts >= 0;
      this.shown_hearts = stats.hearts;

      if ( had_value )
      {
        this.startHeartsPop();
      }
      else
      {
        this.hearts_displayed = stats.hearts;
        this.hearts_count_text?.setText( String( stats.hearts ) );
      }

      this.setShouldBeRendered();
    }

    const timer = heartTimerText( stats, now_ms );
    if ( timer !== this.shown_timer )
    {
      this.shown_timer = timer;
      this.setHudText( this.heart_timer_text, timer );
    }
    this.setHeartPlusVisible( !hearts_infinite && timer !== "MAX" );

    this.avatar_binder?.applyFromStorage( 8 );

    if ( stats.hearts < stats.max_hearts
      && stats.next_heart_at > 0
      && now_ms >= stats.next_heart_at
      && now_ms - this.last_auto_refresh_at > 5000 )
    {
      this.last_auto_refresh_at = now_ms;
      void refreshPlayerFromServer();
    }
  }

  private setHeartPlusVisible( visible: boolean ): void
  {
    const plus = this.heart_plus_image;
    if ( !plus ) return;

    const opacity = visible ? 1 : 0;
    if ( plus.getOpacityImportant() === opacity ) return;

    plus.setOpacityImmediate( opacity );
    plus.forceMakeShouldBeRendered();
  }

  private static readonly HEART_APPEAR_FROM = 0.7;
  private static readonly HEART_APPEAR_OVERSHOOT = 1.1;
  private static readonly HEART_SHRINK_MS = 150;
  private static readonly HEART_APPEAR_UP_MS = 204;
  private static readonly HEART_APPEAR_SETTLE_MS = 144;
  private static readonly HEART_DIGIT_DIM = 0.3;

  private startHeartsPop(): void
  {
    if ( this.hearts_pop_active )
      return;

    this.hearts_pop_active = true;
    this.playHeartPop();
  }

  private playHeartPop(): void
  {
    const icon = this.hearts_icon;
    const text = this.hearts_count_text;

    if ( !icon || !text || this.hearts_displayed === this.shown_hearts )
    {
      this.hearts_pop_active = false;
      return;
    }

    const next = this.hearts_displayed + ( this.shown_hearts > this.hearts_displayed ? 1 : -1 );
    const now = performance.now();

    const from      = new Vector2( MenuScreen.HEART_APPEAR_FROM, MenuScreen.HEART_APPEAR_FROM );
    const overshoot = new Vector2( MenuScreen.HEART_APPEAR_OVERSHOOT, MenuScreen.HEART_APPEAR_OVERSHOOT );
    const normal    = new Vector2( 1, 1 );

    const up_start     = now + MenuScreen.HEART_SHRINK_MS;
    const settle_start = up_start + MenuScreen.HEART_APPEAR_UP_MS;

    const shrink = new TransformScale(
      icon, true, normal, from,
      now, MenuScreen.HEART_SHRINK_MS,
      { easing: Easing.EaseInOutQuad }
    );
    shrink.setOnFinished( () =>
    {
      this.hearts_displayed = next;
      text.setText( String( next ) );
    } );
    icon.addTransform( shrink );

    icon.addTransform( new TransformScale(
      icon, true, from, overshoot,
      up_start, MenuScreen.HEART_APPEAR_UP_MS,
      { easing: Easing.EaseOutQuad }
    ) );

    const settle = new TransformScale(
      icon, true, overshoot, normal,
      settle_start, MenuScreen.HEART_APPEAR_SETTLE_MS,
      { easing: Easing.EaseOutQuad }
    );
    settle.setOnFinished( () => this.playHeartPop() );
    icon.addTransform( settle );

    text.addTransform( new TransformOpacity(
      text, true, 1, MenuScreen.HEART_DIGIT_DIM,
      now, MenuScreen.HEART_SHRINK_MS,
      { easing: Easing.EaseInOutQuad }
    ) );
    text.addTransform( new TransformOpacity(
      text, true, MenuScreen.HEART_DIGIT_DIM, 1,
      up_start, MenuScreen.HEART_APPEAR_UP_MS,
      { easing: Easing.EaseOutQuad }
    ) );
  }

  public name(): string
  {
    return "MenuScreen";
  }

  public render(): void
  {
    const full = this.shouldBeRendered();
    if ( !full && !this.bg_only_paint )
      return;

    this.bg_only_paint = false;

    if ( this.dom_mode )
    {
      this.startRender();
      this.finishRender();
      SafeAreas.syncBothSafeAreaOverlaysDom( this.getDomHost() );
      return;
    }

    // A full repaint means something in the HUD may have changed.
    if ( full )
      this.fg_cache_valid = false;

    if ( this.renderLayered() )
      return;

    // The layer split does not hold (children reordered): paint the whole tree.
    this.invalidateSubtree();
    this.fg_cache_valid = false;
    this.hud_patches.clear();
    this.startRender();
    this.finishRender();
    SafeAreas.renderBothSafeAreaOverlays( this.ctx );
  }

  // Clouds drift all the time and the cart plays an idle animation.
  // Repainting the whole menu for each of
  // those (12 texts and 30 images of HUD on top) ran ~40 times a second and
  // cost 3-15 ms a frame once Android lowered the CPU clocks. Here the layers
  // below the HUD are painted and the HUD is one bitmap blit, re-rendered into
  // the cache only when it actually changed.
  private renderLayered(): boolean
  {
    const children = this.getChildren();
    const background = this.background_elements;
    if ( background.length === 0 || children.length <= background.length )
      return false;

    // The blit assumes the background is drawn first, all of it, before any
    // foreground element.
    for ( let i = 0; i < background.length; i++ )
    {
      if ( children[i] !== background[i] )
        return false;
    }

    this.startRender();
    this.ctx.translate( -this.rect.getW2(), -this.rect.getH2() );

    const cache_stale = !this.fg_cache_valid
      || !this.fg_cache
      || this.fg_cache.width !== this.ctx.canvas.width
      || this.fg_cache.height !== this.ctx.canvas.height
      || ( this.fg_cache_retry_at > 0 && performance.now() >= this.fg_cache_retry_at );

    const ready = ( !cache_stale && this.patchForegroundCache( children, background.length ) )
      || this.rebuildForegroundCache( children, background.length );
    if ( ready )
    {
      for ( const element of background )
      {
        element.invalidateSubtree();
        element.render();
      }
    }

    this.ctx.restore();
    if ( !ready )
      return false;

    // The cache holds canvas pixels 1:1.
    this.ctx.save();
    this.ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    this.ctx.globalAlpha = 1;
    this.ctx.drawImage( this.fg_cache!, 0, 0 );
    this.ctx.restore();

    SafeAreas.renderBothSafeAreaOverlays( this.ctx );
    return true;
  }

  // Runs inside updateBasic, before a dirty screen would invalidate its whole
  // tree. When only layers below the HUD changed (the cart animating, say),
  // drop the full repaint and paint those layers over the cached HUD instead.
  protected override onChildrenUpdated( any_child_dirty: boolean ): void
  {
    if ( !any_child_dirty || this.dirty_before_children || this.isAnimating() )
      return;

    const children = this.getChildren();
    for ( let i = this.background_elements.length; i < children.length; i++ )
    {
      if ( children[i].shouldBeRendered() )
        return;
    }

    this.resetShouldBeRendered();
    this.bg_only_paint = true;
  }

  // Renders every element after the background into fg_cache with the exact
  // transform and alpha the children get on the screen canvas right now.
  private rebuildForegroundCache( children: readonly UIElement[], first_foreground: number ): boolean
  {
    const canvas = this.ctx.canvas;
    if ( !this.fg_cache )
    {
      this.fg_cache = document.createElement( "canvas" );
      this.fg_cache_ctx = this.fg_cache.getContext( "2d" );
    }
    const cache_ctx = this.fg_cache_ctx;
    if ( !cache_ctx )
      return false;

    const transform = this.ctx.getTransform();
    const alpha = this.ctx.globalAlpha;

    if ( this.fg_cache.width !== canvas.width || this.fg_cache.height !== canvas.height )
    {
      this.fg_cache.width = canvas.width;
      this.fg_cache.height = canvas.height;
    }
    cache_ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    cache_ctx.globalAlpha = 1;
    cache_ctx.clearRect( 0, 0, this.fg_cache.width, this.fg_cache.height );
    cache_ctx.setTransform( transform );
    cache_ctx.globalAlpha = alpha;

    const foreground = children.slice( first_foreground );
    this.drawForeground( cache_ctx, foreground );
    this.hud_patches.clear();

    // Images still loading draw nothing; rebuild shortly until they land.
    this.fg_cache_valid = true;
    this.fg_cache_retry_at = foreground.some( element => subtreeHasUnloadedImages( element ) )
      ? performance.now() + MenuScreen.FG_CACHE_RETRY_MS
      : 0;    return true;
  }

  private drawForeground( cache_ctx: CanvasRenderingContext2D, foreground: readonly UIElement[] ): void
  {
    for ( const element of foreground )
      element.setContextRecursive( cache_ctx );
    try
    {
      for ( const element of foreground )
      {
        element.invalidateSubtree();
        element.render();
      }
    }
    finally
    {
      for ( const element of foreground )
        element.setContextRecursive( this.ctx );
    }
  }

  private setHudText( text_element: UITextElement | undefined, text: string ): void
  {
    if ( !text_element )
      return;

    if ( this.dom_mode || !this.fg_cache_valid )
    {
      text_element.setText( text );
      return;
    }

    text_element.setTextWithoutRepaint( text );
    this.hud_patches.add( text_element );
    this.bg_only_paint = true;
  }

  private patchForegroundCache( children: readonly UIElement[], first_foreground: number ): boolean
  {
    if ( this.hud_patches.size === 0 )
      return true;

    const patches = [ ...this.hud_patches ];
    this.hud_patches.clear();

    const cache = this.fg_cache;
    const cache_ctx = this.fg_cache_ctx;
    if ( !cache || !cache_ctx )
      return false;

    const dpr = Game.getDpr();
    const pad = MenuScreen.HUD_PATCH_PAD;

    cache_ctx.save();
    cache_ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    cache_ctx.globalAlpha = 1;
    cache_ctx.beginPath();
    for ( const element of patches )
    {
      let x0 = Infinity;
      let y0 = Infinity;
      let x1 = -Infinity;
      let y1 = -Infinity;
      for ( const part of [ element, element.getParentElement() ] )
      {
        if ( !part )
          continue;
        const layout = part.getAbsoluteLayoutForHtml();
        const rect = part.getRect();
        x0 = Math.min( x0, layout.x );
        y0 = Math.min( y0, layout.y );
        x1 = Math.max( x1, layout.x + rect.getW() * layout.scale_x );
        y1 = Math.max( y1, layout.y + rect.getH() * layout.scale_y );
      }
      const left = Math.floor( ( x0 - pad ) * dpr );
      const top = Math.floor( ( y0 - pad ) * dpr );
      cache_ctx.rect( left, top, Math.ceil( ( x1 + pad ) * dpr ) - left, Math.ceil( ( y1 + pad ) * dpr ) - top );
    }
    cache_ctx.clip();
    cache_ctx.clearRect( 0, 0, cache.width, cache.height );
    cache_ctx.setTransform( this.ctx.getTransform() );
    cache_ctx.globalAlpha = this.ctx.globalAlpha;
    try
    {
      this.drawForeground( cache_ctx, children.slice( first_foreground ) );
    }
    finally
    {
      cache_ctx.restore();
    }
    return true;
  }

  public update( now: number ): void
  {
    this.updateClouds( now );

    if ( now >= this.next_ui_sync_at )
    {
      this.next_ui_sync_at = now + 1000;
      this.syncTopBar();
      this.syncRemoveAdsButton();
      this.syncEventUnlocks();
      this.syncRewardBadges();
      this.updateDailyTasksTimer();
      this.updateAdventureTimer();
      this.updateDailyChallengeTimer();
      this.updateChainOfferTimer();
      this.syncChainVideoButton();
      this.syncTournament();
      this.syncOnePlusOneOfferButton();
      this.syncOnePlusTwoOfferButton();
      this.syncMysteryBoxButton();
      this.syncKeyHuntButton();
      this.syncPrizeShotButton();
    }

    this.maybeStartFeatureTutorial();
    // The screen dirtied itself above (top bar, timers): a full repaint.
    this.dirty_before_children = this.shouldBeRendered();
    this.updateBasic( now );
  }

  private paint_shift_accum = 0;

  private requestPaintForShift( shift: number ): void
  {
    this.paint_shift_accum += Math.abs( shift );

    // A dialog dims this canvas but does not replace it. Keep painting every
    // frame while it is open so animated clouds remain live underneath the
    // translucent overlay instead of waiting for the paint threshold.
    if ( DialogManager.isAnyDialogOpened() )
    {
      this.paint_shift_accum = 0;
      this.bg_only_paint = true;
      return;
    }

    if ( this.paint_shift_accum < 1 / Game.getDpr() )
      return;

    this.paint_shift_accum = 0;
    // Only background layers moved: no need to dirty (and repaint) the HUD.
    this.bg_only_paint = true;
  }

  private cloud_anims: Animation[] = [];

  private startCloudDomDrift(): void
  {
    for ( const c of this.clouds )
    {
      const pos = c.el.pixelPosImportant();
      const anim = startDomDrift( c.el.getDomNode(), pos.x, pos.y, c.w, this.cloud_wrap_w, c.speed );
      if ( anim )
        this.cloud_anims.push( anim );
    }
  }

  private updateClouds( now: number ): void
  {
    if ( this.clouds.length === 0 )
      return;

    if ( !GraphicsSettings.profile().sky_animation )
    {
      this.clouds_last_now = now;
      for ( const anim of this.cloud_anims )
        cancelDomAnimation( anim );
      this.cloud_anims = [];
      return;
    }

    if ( this.dom_mode )
    {
      if ( this.cloud_anims.length === 0 )
        this.startCloudDomDrift();
      return;
    }

    const dt = this.clouds_last_now > 0 ? Math.min( 0.05, ( now - this.clouds_last_now ) / 1000 ) : 0;
    this.clouds_last_now = now;

    if ( dt === 0 )
      return;

    let max_move = 0;

    for ( const c of this.clouds )
    {
      const pos = c.el.pixelPosImportant();
      const movement = c.speed * dt;
      let x = pos.x + movement;

      if ( x > this.cloud_wrap_w )
        x = -c.w;

      c.el.setPixelPos( new Vector2( x, pos.y ), true );
      max_move = Math.max( max_move, Math.abs( movement ) );
    }

    this.requestPaintForShift( max_move );
  }
}
